import React, { useState, useEffect, useRef } from 'react';
import { 
  TextField, Button, CircularProgress, Autocomplete, 
  MenuItem, Select, FormControl, InputLabel, Alert, AlertTitle,
  Divider, Typography, Box, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, ButtonGroup, Chip, Avatar, Grid
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ViewListIcon from '@mui/icons-material/ViewList';
import TableRowsIcon from '@mui/icons-material/TableRows';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import WarningIcon from '@mui/icons-material/Warning';
import { styled } from '@mui/material/styles';
import StandaloneTreatmentApprovalModal from './StandaloneTreatmentApprovalModal';
import UpgradeApprovalModal from './UpgradeApprovalModal';
import PackageSchedulingTableView from './PackageSchedulingTableView';
import PackageSchedulingCalendarView from './PackageSchedulingCalendarView';
import EditBookingModal from './EditBookingModal';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiDelete, apiPatch } from '../utils/api';
import { authaltFetch, AUTHALT_API_BASE } from '../services/authaltApi';

// API Configuration - token fetched dynamically from backend
const GUEST_API_BASE = AUTHALT_API_BASE;
const BOOKING_PREFS_STORAGE_PREFIX = 'packageSchedulingBookingPrefs:';

const loadBookingPrefsFromStorage = (guestId) => {
  if (!guestId) return null;
  try {
    const raw = localStorage.getItem(`${BOOKING_PREFS_STORAGE_PREFIX}${guestId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveBookingPrefsToStorage = (guestId, prefs) => {
  if (!guestId) return;
  localStorage.setItem(`${BOOKING_PREFS_STORAGE_PREFIX}${guestId}`, JSON.stringify(prefs));
};

const isActiveWellnessAdvisor = (therapist) =>
  therapist?.role === 'Wellness Advisor' && therapist.is_active !== false;

const buildWellnessAdvisorList = (therapistsObj = {}) =>
  Object.values(therapistsObj)
    .filter(isActiveWellnessAdvisor)
    .map((therapist) => ({
      name: therapist.name,
      id: therapist.id || therapist.name,
      email: therapist.email || '',
    }));

const isConsultationTreatment = (treatment, availableTreatments = []) => {
  const metaType = (
    treatment?.type ||
    availableTreatments.find((t) => t.name === treatment?.name)?.type ||
    ''
  ).toUpperCase();
  const name = (treatment?.name || '').toUpperCase();
  return metaType.includes('CONSULTATION') || name.includes('CONSULTATION');
};

const getTreatmentCatalogMeta = (treatment, availableTreatments = []) =>
  availableTreatments.find((t) => t.name === treatment?.name) || treatment || {};

const isBcaOrBodyCompositionTreatment = (treatment) => {
  const name = (treatment?.name || '').toUpperCase();
  return name.includes('BCA') || name.includes('BODY COMPOSITION');
};

const isDiagnosticsTreatment = (treatment, availableTreatments = []) => {
  const metaType = (
    treatment?.type ||
    availableTreatments.find((t) => t.name === treatment?.name)?.type ||
    ''
  ).toUpperCase();
  const name = (treatment?.name || '').toUpperCase();
  return metaType.includes('DIAGNOSTIC') || name.includes('DIAGNOSTIC');
};

/** Any unbooked schedule line item can be moved to another day within the guest stay. */
const isScheduleDateEditableTreatment = () => true;

const getScheduleDateEditLabel = (treatment, availableTreatments = []) => {
  if (isBcaOrBodyCompositionTreatment(treatment)) return 'BCA date';
  if (isDiagnosticsTreatment(treatment, availableTreatments)) return 'Diagnostics date';
  if (isConsultationTreatment(treatment, availableTreatments)) return 'Consultation date';
  return 'Treatment date';
};

const getScheduleDateEditHelperText = (treatment, availableTreatments = []) => {
  if (isConsultationTreatment(treatment, availableTreatments)) {
    return 'Move to another day within the stay (including checkout day)';
  }
  if (isBcaOrBodyCompositionTreatment(treatment)) {
    const slots = getTreatmentCatalogMeta(treatment, availableTreatments).allowed_start_times;
    if (slots && String(slots).trim()) {
      return `Allowed start times: ${String(slots).trim()}. Change the day if needed, then Retry to book.`;
    }
    return 'Move BCA to another day within the stay, then Retry to book. Set allowed start times in Admin if slots fail.';
  }
  if (isDiagnosticsTreatment(treatment, availableTreatments)) {
    return 'Move to another day within the stay, then Retry to book';
  }
  return 'Move to another day within the guest stay, then Save and Retry to book';
};

// Treatment type color mapping
const TYPE_COLORS = {
  'WELLNESS': 'bg-green-100 text-green-800',
  'PHYSIOTHERAPY': 'bg-blue-100 text-blue-800',
  'DOCTOR CONSULTATION': 'bg-purple-100 text-purple-800',
  'THERAPY': 'bg-teal-100 text-teal-800',
  'AYURVEDA': 'bg-orange-100 text-orange-800',
  'FITNESS': 'bg-red-100 text-red-800',
  'NUTRITION': 'bg-yellow-100 text-yellow-800',
  'DEFAULT': 'bg-gray-100 text-gray-800'
};

const getTypeColor = (type) => {
  if (!type) return TYPE_COLORS.DEFAULT;
  const upperType = type.toUpperCase();
  return TYPE_COLORS[upperType] || TYPE_COLORS.DEFAULT;
};

// Format time in minutes to HH:MM string
// Handles both integer (minutes) and string (HH:MM) formats
const formatTime = (time) => {
  if (time === null || time === undefined) {
    return 'Pending';
  }
  
  // If already a string in HH:MM format, return as is
  if (typeof time === 'string') {
    return time;
  }
  
  // If it's a number, convert minutes to HH:MM
  if (typeof time === 'number' && !isNaN(time)) {
    const hours = Math.floor(time / 60);
    const mins = time % 60;
    return `${hours}:${String(mins).padStart(2, '0')}`;
  }
  
  return 'Pending';
};

/** Minutes from midnight — used for avoidTimes in schedule preview API */
const parseTimeToMinutes = (time) => {
  if (time == null) return null;
  if (typeof time === 'number' && !Number.isNaN(time)) return time;
  if (typeof time === 'string') {
    const [hours, mins] = time.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(mins)) return null;
    return hours * 60 + mins;
  }
  return null;
};

const slotDedupeKey = (slot) =>
  `${parseTimeToMinutes(slot.startTime)}-${slot.therapist}-${slot.room}`;

const AFTERNOON_START_MINUTES = 14 * 60; // 2:00 PM — after lunch

const classifySlotPeriod = (startTime) => {
  const minutes = parseTimeToMinutes(startTime);
  if (minutes == null) return 'morning';
  return minutes >= AFTERNOON_START_MINUTES ? 'afternoon' : 'morning';
};

// Generate deterministic ID for schedule items
// Format: {guest_id}_day{day}_{treatment_name}_order{order}
// Example: "AWC002913_day2_ABHYANGA_order6"
// This ensures frontend and backend always use the same ID
const generateDeterministicId = (guestId, dayNumber, treatmentName, order) => {
  // Sanitize treatment name: replace spaces and special chars with underscores
  const sanitized = treatmentName.replace(/[^a-zA-Z0-9]/g, '_');
  return `${guestId}_day${dayNumber}_${sanitized}_order${order}`;
};

const formatDateISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addDaysISO = (iso, offset) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + offset);
  return formatDateISO(dt);
};

const dayDateFromStart = (startDateISO, dayNumber) =>
  addDaysISO(startDateISO, dayNumber - 1);

const dayNumberFromDate = (startDateISO, targetDateISO) => {
  const start = new Date(`${startDateISO}T12:00:00`);
  const target = new Date(`${targetDateISO}T12:00:00`);
  return Math.round((target - start) / 86400000) + 1;
};

const moveConsultationInSchedule = (
  scheduleDays,
  sourceDayIndex,
  treatmentIndex,
  newDateISO,
  startDateISO,
  guestIdValue
) => {
  if (!scheduleDays?.length || !startDateISO || !guestIdValue) return null;

  let updatedSchedule = scheduleDays.map((day) => ({
    ...day,
    treatments: [...day.treatments],
  }));

  const [movedTreatment] = updatedSchedule[sourceDayIndex].treatments.splice(treatmentIndex, 1);
  if (!movedTreatment) return null;

  if (updatedSchedule[sourceDayIndex].treatments.length === 0) {
    updatedSchedule = updatedSchedule.filter((_, index) => index !== sourceDayIndex);
  }

  const targetDayNumber = dayNumberFromDate(startDateISO, newDateISO);
  let targetDayIndex = updatedSchedule.findIndex((day) => day.date === newDateISO);
  if (targetDayIndex < 0) {
    updatedSchedule = [
      ...updatedSchedule,
      {
        day: targetDayNumber,
        date: newDateISO,
        dayOfWeek: new Date(`${newDateISO}T12:00:00`).toLocaleDateString('en-US', {
          weekday: 'long',
        }),
        treatments: [],
      },
    ].sort((a, b) => a.date.localeCompare(b.date));
    targetDayIndex = updatedSchedule.findIndex((day) => day.date === newDateISO);
  }

  const targetDay = updatedSchedule[targetDayIndex];
  const newOrder = movedTreatment.order || targetDay.treatments.length + 1;
  const newInclusionId = generateDeterministicId(
    guestIdValue,
    targetDay.day || targetDayNumber,
    movedTreatment.name,
    newOrder
  );

  targetDay.treatments.push({
    ...movedTreatment,
    inclusionId: newInclusionId,
    order: newOrder,
  });

  return {
    schedule: updatedSchedule,
    oldInclusionId: movedTreatment.inclusionId,
    newInclusionId,
    newDayIndex: targetDayIndex,
    newTreatmentIndex: targetDay.treatments.length - 1,
  };
};

/** Add DB bookings that are missing from the saved package schedule (e.g. after add-on book). */
const mergeOrphanBookingsIntoSchedule = (scheduleData, bookings, guestIdValue) => {
  if (!scheduleData?.schedule?.length || !bookings?.length || !guestIdValue) {
    return scheduleData;
  }

  const updated = {
    ...scheduleData,
    schedule: scheduleData.schedule.map((day) => ({ ...day, treatments: [...day.treatments] }))
  };

  const activeBookings = bookings.filter(
    (b) => b.status !== 'CANCELLED' && b.status !== 'CANCELED'
  );

  activeBookings.forEach((booking) => {
    const treatmentName = booking.treatment || booking.treatmentName;
    if (!treatmentName || !booking.date) return;

    const day = updated.schedule.find((d) => d.date === booking.date);
    if (!day) return;

    const entitlementName = booking.original_treatment || booking.originalTreatment || '';
    const entitlementLine = entitlementName && entitlementName !== treatmentName
      ? day.treatments.find((t) => t.name === entitlementName && !t.isManuallyAdded)
      : null;

    // A doctor upgrade renames the booking. Keep it on the package line
    // instead of leaving the original treatment and adding a second row.
    if (entitlementLine) {
      const upgradedLine = {
        ...entitlementLine,
        name: treatmentName,
        duration: booking.duration || entitlementLine.duration,
        isUpgraded: true,
        inclusionId: generateDeterministicId(
          guestIdValue,
          day.day,
          treatmentName,
          entitlementLine.order || 1,
        ),
      };
      day.treatments = day.treatments
        .map((t) => (t === entitlementLine ? upgradedLine : t))
        .filter((t) => t === upgradedLine || t.name !== treatmentName);
      return;
    }

    const alreadyListed = day.treatments.some((t) => t.name === treatmentName);
    if (alreadyListed) return;

    const order = Math.max(0, ...day.treatments.map((t) => t.order || 0)) + 1;
    day.treatments.push({
      inclusionId: generateDeterministicId(guestIdValue, day.day, treatmentName, order),
      name: treatmentName,
      duration: booking.duration || 50,
      prepTime: 10,
      cleanupTime: 15,
      type: 'WELLNESS',
      isManuallyAdded: true,
      isSyncedFromBooking: true,
      order
    });
  });

  return updated;
};

/** Rebuild bookingResults from schedule items + live DB bookings (source of truth). */
const buildBookingResultsFromSchedule = (scheduleData, bookings, sessionFailures = []) => {
  if (!scheduleData?.schedule) {
    return {
      success: false,
      bookings: [],
      failures: [],
      booked_count: 0,
      failed_count: 0,
      summary: { total: 0, booked: 0, failed: 0 }
    };
  }

  const activeBookings = (bookings || []).filter(
    (b) => b.status !== 'CANCELLED' && b.status !== 'CANCELED'
  );

  const resultBookings = [];
  const failures = [];
  const usedBookingIds = new Set();

  scheduleData.schedule.forEach((day) => {
    day.treatments.forEach((treatment) => {
      if (!treatment.name) return;

      const sessionFailure = sessionFailures.find((f) => {
        if (f.date !== day.date) return false;
        if (treatment.inclusionId && f.inclusionId) {
          return f.inclusionId === treatment.inclusionId;
        }
        return f.treatmentName === treatment.name;
      });

      const booking = activeBookings.find((b) => {
        if (b.date !== day.date) return false;
        const bookedName = b.treatment || b.treatmentName;
        const entitlement = b.original_treatment || b.originalTreatment;
        if (treatment.inclusionId && b.inclusionId) {
          return b.inclusionId === treatment.inclusionId;
        }
        return bookedName === treatment.name || entitlement === treatment.name;
      });

      if (booking) {
        const bid = booking.bookingId || booking.id;
        if (bid && usedBookingIds.has(bid)) return;
        if (bid) usedBookingIds.add(bid);
        resultBookings.push({
          ...booking,
          inclusionId: treatment.inclusionId,
          treatmentName: treatment.name,
          treatment: booking.treatment || treatment.name,
          date: day.date,
          startTime: booking.startTime || booking.start_time,
          endTime: booking.endTime || booking.end_time,
          therapist: booking.therapist,
          room: booking.room,
          bookingId: bid,
          id: bid,
          duration: booking.duration,
          custom_duration: booking.custom_duration,
          status: booking.status,
          treatmentInstruction: booking.treatmentInstruction || booking.treatment_instruction || '',
          generalInstruction: booking.generalInstruction || booking.general_instruction || '',
          isExisting: true
        });
      } else if (sessionFailure) {
        failures.push({
          inclusionId: treatment.inclusionId || sessionFailure.inclusionId,
          treatmentName: treatment.name,
          date: day.date,
          reason: sessionFailure.reason,
          type: sessionFailure.type
        });
      }
    });
  });

  const total = scheduleData.schedule.reduce(
    (sum, day) => sum + day.treatments.filter((t) => t.name).length,
    0
  );

  return {
    success: resultBookings.length > 0 && failures.length === 0,
    bookings: resultBookings,
    failures,
    booked_count: resultBookings.length,
    failed_count: failures.length,
    summary: {
      total,
      booked: resultBookings.length,
      failed: failures.length,
      success_rate: total > 0 ? `${((resultBookings.length / total) * 100).toFixed(1)}%` : '0%'
    }
  };
};

// authalt guestlistdatav3 returns gender as "M" / "F"; dropdown uses Male / Female / Other
const normalizeGuestGenderForSelect = (gender) => {
  const g = String(gender || '').trim();
  if (!g) return '';
  const upper = g.toUpperCase();
  if (upper === 'M' || upper.startsWith('MALE')) return 'Male';
  if (upper === 'F' || upper.startsWith('FEMALE')) return 'Female';
  if (upper === 'OTHER' || upper === 'O') return 'Other';
  return g;
};

const PackageScheduling = () => {
  // Form state
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [nightsNumber, setNightsNumber] = useState(7); // Just the number (1-50)
  const [selectedNights, setSelectedNights] = useState(''); // With "N" suffix for API
  const [availableNights, setAvailableNights] = useState([3, 5, 6, 7, 10, 14, 21, 28]); // Valid nights from retreat packages
  const [showNightsWarning, setShowNightsWarning] = useState(false);
  const [pendingNightsValue, setPendingNightsValue] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [guestId, setGuestId] = useState('');
  const [externalBookingId, setExternalBookingId] = useState(''); // External resort booking ID (BKG...)
  const [guestGender, setGuestGender] = useState('');  // M/F/Other - for gender-specific treatments
  const [guestRoom, setGuestRoom] = useState('');
  const [guestRoomTherapyAvailable, setGuestRoomTherapyAvailable] = useState(false);
  const [guestCheckinETA, setGuestCheckinETA] = useState(''); // ETA for arrival time
  const [guestCheckoutETD, setGuestCheckoutETD] = useState(''); // ETD for departure time
  const [guestArrivalDate, setGuestArrivalDate] = useState(''); // Check-in date
  const [guestDepartureDate, setGuestDepartureDate] = useState(''); // Check-out date
  const [assignedDoctor, setAssignedDoctor] = useState('');
  const [startDate, setStartDate] = useState('');
  const [guests, setGuests] = useState([]);
  const [allGuests, setAllGuests] = useState([]); // All fetched guests
  const [apiCounts, setApiCounts] = useState({ inHouse: 0, upcoming: 0 }); // Store API-provided counts
  const [guestFilter, setGuestFilter] = useState(() => {
    // Force default to 'inhouse' - migrate from old 'upcoming' default
    const saved = localStorage.getItem('packageSchedulingGuestFilter');
    if (!saved) {
      localStorage.setItem('packageSchedulingGuestFilter', 'inhouse');
      return 'inhouse';
    }
    return saved;
  });
  const [selectedGuestForInfo, setSelectedGuestForInfo] = useState(null);
  const [guestInfoDialogOpen, setGuestInfoDialogOpen] = useState(false);
  const [wellnessAdvisors, setWellnessAdvisors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [allRoomsData, setAllRoomsData] = useState({});
  const [config, setConfig] = useState(null); // Full config for EditBookingModal
  
  // Auto-filled from guest data (hybrid mode)
  const [assignedPackage, setAssignedPackage] = useState(null); // {name, nights, programId}
  const [assignedAdvisor, setAssignedAdvisor] = useState(''); // consultingDoctor from API
  const staleAdvisorAlertedRef = useRef(null);
  const scheduleLoadGuestRef = useRef('');
  const [packageLocked, setPackageLocked] = useState(false); // CRM-assigned package — cannot change
  const [showPackageDropdown, setShowPackageDropdown] = useState(false);
  const [showAdvisorDropdown, setShowAdvisorDropdown] = useState(false);
  
  // Schedule state
  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [generalInstruction, setGeneralInstruction] = useState('');
  const [treatmentInstructions, setTreatmentInstructions] = useState({});
  
  // Treatment editing state
  const [editingTreatment, setEditingTreatment] = useState(null); // {dayIndex, treatmentIndex}
  const [availableTreatments, setAvailableTreatments] = useState([]);
  
  // Booking preferences
  const [therapistGenderPreference, setTherapistGenderPreference] = useState('');
  const [therapistLevel, setTherapistLevel] = useState('');
  const [roomPreference, setRoomPreference] = useState(null);
  
  // View mode state
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('packageSchedulingViewMode') || 'list';
  });
  
  // Booking state
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [bookingResults, setBookingResults] = useState(null);
  const [existingBookings, setExistingBookings] = useState([]); // Track already-booked treatments
  const [hasAttemptedBooking, setHasAttemptedBooking] = useState(false); // Track if user clicked "Book All Appointments"
  // Outcome shown inside the booking modal after the API responds.
  // Shape: { type: 'success' | 'partial' | 'error',
  //          bookedCount, alreadyBooked, failedCount, totalAttempted,
  //          successRate, failures: [{treatmentName, date, reason}], message }
  const [bookingOutcome, setBookingOutcome] = useState(null);
  // When booking runs in multiple HTTP batches (long schedules), show batch index in modal.
  const [bookingChunkProgress, setBookingChunkProgress] = useState(null); // { current, total, label }
  
  // Standalone treatment approval state (for manually added treatments)
  const [showStandaloneApprovalModal, setShowStandaloneApprovalModal] = useState(false);
  const [standaloneTreatmentData, setStandaloneTreatmentData] = useState(null);
  const [standaloneApprovals, setStandaloneApprovals] = useState({}); // {inclusionId: approvalData}
  const [upgradeApprovals, setUpgradeApprovals] = useState({}); // {inclusionId: upgradeApprovalData}
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradePreview, setUpgradePreview] = useState(null);
  const [pendingUpgradeChange, setPendingUpgradeChange] = useState(null);
  
  // Edit booking modal state (for editing booked treatments)
  const [showEditBookingModal, setShowEditBookingModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  
  // Retry booking modal state (for failed treatments)
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [retryTreatmentData, setRetryTreatmentData] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [retryLoading, setRetryLoading] = useState(false);
  /** Scheduler error strings from last preview attempts (shown when no slots found) */
  const [retryFailureHints, setRetryFailureHints] = useState([]);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper functions for date parsing and filtering
  const getFirstNonEmpty = (obj, keys) => {
    for (const k of keys) {
      const v = obj[k];
      if (v !== null && v !== undefined && String(v).trim() !== '') return v;
    }
    return null;
  };

  const toISO = (s) => {
    if (!s) return null;
    const str = String(s).trim();
    // Support YYYY-MM-DD first
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    // Try DD/MM/YYYY or DD-MM-YYYY
    const m = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
    if (m) {
      const d = String(m[1]).padStart(2,'0');
      const mo = String(m[2]).padStart(2,'0');
      const y = String(m[3]).length === 2 ? `20${m[3]}` : String(m[3]);
      return `${y}-${mo}-${d}`;
    }
    // Fallback Date parse
    const t = Date.parse(str);
    if (!Number.isNaN(t)) return new Date(t).toISOString().split('T')[0];
    return null;
  };

  // Match backend guest_stay_validation + other desks (CRM may use ArrivalDate, check_in, etc.)
  const GUEST_CHECKIN_KEYS = [
    'checkIn', 'checkin', 'check_in', 'startDate', 'start', 'arrivalDate', 'ArrivalDate',
  ];
  const GUEST_CHECKOUT_KEYS = [
    'checkOut', 'checkout', 'check_out', 'endDate', 'end', 'departureDate', 'DepartureDate',
  ];

  const getCheckInISO = (g) => toISO(getFirstNonEmpty(g, GUEST_CHECKIN_KEYS));
  const getCheckOutISO = (g) => toISO(getFirstNonEmpty(g, GUEST_CHECKOUT_KEYS));

  const formatGuestFieldLabel = (key) => {
    const labels = {
      arrivalDate: 'Check-in Date',
      ArrivalDate: 'Check-in Date',
      departureDate: 'Check-out Date',
      DepartureDate: 'Check-out Date',
      arrival: 'Check-in',
      departure: 'Check-out',
    };
    return labels[key] || key;
  };

  /** Nights between check-in and check-out (e.g. Jun 25 → Jul 3 = 8). */
  const computeStayNights = (checkInISO, checkOutISO) => {
    if (!checkInISO || !checkOutISO) return null;
    const ci = new Date(checkInISO);
    const co = new Date(checkOutISO);
    if (Number.isNaN(ci.getTime()) || Number.isNaN(co.getTime())) return null;
    const diff = Math.round((co - ci) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : null;
  };

  const capNightsToGuestStay = (packageNights, stayNights, contextLabel = 'Package') => {
    if (!stayNights || !packageNights || packageNights <= stayNights) {
      return packageNights;
    }
    setTimeout(() => {
      alert(
        `${contextLabel} is ${packageNights} nights but the guest stay is only ${stayNights} nights ` +
        `(check-in ${guestArrivalDate || '?'} → check-out ${guestDepartureDate || '?'}).\n\n` +
        `Nights will be capped to ${stayNights} for scheduling. Pick a shorter package variant if needed.`
      );
    }, 100);
    return stayNights;
  };

  const formatDateRange = (g) => {
    const ci = getCheckInISO(g);
    const co = getCheckOutISO(g);
    if (!ci || !co) return '';
    
    const formatDate = (isoDate) => {
      const date = new Date(isoDate);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    
    return `${formatDate(ci)} - ${formatDate(co)}`;
  };

  const isInHouse = (g, d) => {
    const ci = getCheckInISO(g); 
    const co = getCheckOutISO(g);
    if (!ci || !co) return false; 
    return ci <= d && d <= co;
  };

  const isUpcoming = (g, d) => {
    const ci = getCheckInISO(g); 
    if (!ci) return false; 
    return ci > d;
  };

  const today = new Date().toISOString().split('T')[0];

  const stripHtmlTags = (html) => {
    if (!html) return '';
    const text = String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return text;
  };

  // Fetch packages on mount (local retreat packages only)
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        
        // Fetch local retreat packages
        const response = await apiGet('/api/retreat-packages/?is_active=true');
        if (!response.ok) throw new Error('Failed to fetch packages');
        
        const localPackages = await response.json();
        
        // Transform local packages for the UI
        const transformedPackages = localPackages.map(pkg => ({
          id: pkg.id,
          recordId: pkg.id,
          packageName: pkg.name,
          name: pkg.name,
          nights: `${pkg.nights}N`,
          nightsNumber: pkg.nights,
          description: pkg.description,
          category: pkg.category,
          totalTreatments: pkg.items ? pkg.items.length : 0,
          items: pkg.items
        }));
        
        console.log(`✅ Loaded ${transformedPackages.length} retreat packages`);
        setPackages(transformedPackages);
        
      } catch (err) {
        console.error('Error fetching packages:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  // Fetch guests for autocomplete
  useEffect(() => {
    const fetchGuests = async () => {
      try {
        // Fetch all guest types using the new API endpoints with POST
        const types = ['InHouse', 'TodaysArrival', 'TodaysDeparture', 'Next4Days'];
        let allGuestsData = [];
        let counts = { inHouse: 0, todaysArrival: 0, todaysDeparture: 0, arrivalNext4Days: 0 };
        
        for (const type of types) {
          try {
            const res = await authaltFetch('/guestlistdatav3', {
              type,
              index: 'atmantan'
            });
            if (res.ok) {
              const response = await res.json();
              const categoryKey = type === 'InHouse' ? 'inHouse' :
                                 type === 'TodaysArrival' ? 'todaysArrival' :
                                 type === 'TodaysDeparture' ? 'todaysDeparture' : 'arrivalNext4Days';
              const guestsFromType = response[categoryKey]?.data || [];
              counts[categoryKey] = response[categoryKey]?.count || 0;
              allGuestsData = allGuestsData.concat(guestsFromType);
            }
          } catch (e) {
            console.error(`Failed to fetch ${type}:`, e);
          }
        }
        
        // Store API counts
        setApiCounts({
          inHouse: counts.inHouse,
          upcoming: counts.todaysArrival + counts.arrivalNext4Days
        });
        
        // Remove duplicates by guestId
        const uniqueGuests = Array.from(
          new Map(allGuestsData.map(g => [g.guestId || g.id, g])).values()
        );
        
        // Sort guests alphabetically by name; normalize gender for dropdown auto-fill
        const sorted = uniqueGuests
          .map((g) => ({
            ...g,
            gender: normalizeGuestGenderForSelect(g.gender || g.Gender || ''),
          }))
          .filter(g => (g.guestName || g.name || '').trim() !== '') // Only show guests with names
          .sort((a, b) => {
            const nameA = (a.guestName || a.name || '').toLowerCase();
            const nameB = (b.guestName || b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
        setAllGuests(sorted);
      } catch (err) {
        console.error('Failed to fetch guests:', err);
      }
    };

    fetchGuests();
  }, []);

  const compareGuestsByName = (a, b) => {
    const nameA = (a.guestName || a.name || '').toLowerCase();
    const nameB = (b.guestName || b.name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  };

  const compareGuestsByArrival = (a, b) => {
    const dateA = getCheckInISO(a) || '9999-12-31';
    const dateB = getCheckInISO(b) || '9999-12-31';
    if (dateA !== dateB) return dateA.localeCompare(dateB);
    return compareGuestsByName(a, b);
  };

  // Filter guests based on selected filter (upcoming/inhouse)
  useEffect(() => {
    if (guestFilter === 'upcoming') {
      setGuests(allGuests.filter(g => isUpcoming(g, today)).sort(compareGuestsByArrival));
    } else if (guestFilter === 'inhouse') {
      setGuests(allGuests.filter(g => isInHouse(g, today)));
    } else {
      setGuests(allGuests);
    }
  }, [guestFilter, allGuests]);

  // Persist guest filter selection to localStorage
  useEffect(() => {
    localStorage.setItem('packageSchedulingGuestFilter', guestFilter);
  }, [guestFilter]);

  // One-time migration: Reset filter to 'inhouse' as new default
  useEffect(() => {
    const migrated = localStorage.getItem('packageSchedulingFilterMigrated');
    if (!migrated) {
      // First time after update - set to 'inhouse'
      localStorage.setItem('packageSchedulingGuestFilter', 'inhouse');
      localStorage.setItem('packageSchedulingFilterMigrated', 'true');
      setGuestFilter('inhouse');
    }
  }, []); // Run once on mount

  // Debug: Monitor selectedNights changes
  useEffect(() => {
    console.log('selectedNights changed:', selectedNights);
    console.log('nightsNumber changed:', nightsNumber);
  }, [selectedNights, nightsNumber]);

  // Fetch Wellness Advisors and Rooms from config
  useEffect(() => {
    let isMounted = true; // Prevent state updates if component unmounts
    
    const fetchConfig = async () => {
      try {
        const response = await apiGet('/api/config');
        if (response.ok && isMounted) {
          const data = await response.json();
          
          // Store full config for EditBookingModal
          setConfig(data);
          
          // Active Wellness Advisors only (deleted/inactive staff excluded)
          const advisors = buildWellnessAdvisorList(data.therapists || {});
          setWellnessAdvisors(advisors);
          console.log('Wellness Advisors loaded:', advisors);
          
          // Store full rooms data for room matching
          setAllRoomsData(data.rooms || {});
          
          // Get rooms list
          const roomsList = Object.keys(data.rooms || {}).map(roomName => ({
            name: roomName,
            details: data.rooms[roomName],
            location: data.rooms[roomName]?.location || ''
          }));
          setRooms(roomsList);
          console.log('Rooms loaded:', roomsList.length);
          
          // Load treatments for editing dropdown
          const treatments = Array.isArray(data.treatments) ? data.treatments : [];
          setAvailableTreatments(treatments);
          console.log('Treatments loaded:', treatments.length);
        }
      } catch (err) {
        console.error('Failed to fetch config:', err);
      }
    };

    fetchConfig();
    
    return () => {
      isMounted = false; // Cleanup: prevent state updates after unmount
    };
  }, []);

  // Persist view mode preference
  useEffect(() => {
    localStorage.setItem('packageSchedulingViewMode', viewMode);
  }, [viewMode]);

  // Persist booking preferences (gender, level, room) per guest across refresh
  useEffect(() => {
    if (!guestId) return;

    const prefs = {
      therapistGenderPreference,
      therapistLevel,
      roomPreferenceName: roomPreference?.name || null,
    };
    saveBookingPrefsToStorage(guestId, prefs);

    const timer = setTimeout(() => {
      apiPatch(
        `/api/package-schedules/guest/${encodeURIComponent(guestId)}/booking-preferences`,
        {
          therapist_gender_preference: therapistGenderPreference || null,
          therapist_level: therapistLevel || null,
          room_preference: roomPreference?.name || null,
        }
      ).catch(() => {
        // No saved schedule yet — localStorage still preserves prefs on refresh
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [guestId, therapistGenderPreference, therapistLevel, roomPreference]);

  // Re-apply saved room preference once rooms list has loaded (async config fetch)
  useEffect(() => {
    if (!guestId || rooms.length === 0) return;
    const storedPrefs = loadBookingPrefsFromStorage(guestId);
    if (!storedPrefs?.roomPreferenceName) return;
    const roomObj = rooms.find((r) => r.name === storedPrefs.roomPreferenceName);
    if (roomObj) {
      setRoomPreference((current) => (current?.name === roomObj.name ? current : roomObj));
    }
  }, [guestId, rooms]);

  const clearStaleWellnessAdvisor = (doctorName, guestIdForAlert) => {
    if (guestIdForAlert && staleAdvisorAlertedRef.current !== guestIdForAlert) {
      staleAdvisorAlertedRef.current = guestIdForAlert;
      window.alert(
        `Wellness Advisor "${doctorName}" is no longer available. Please select a current advisor.`
      );
    }
    setAssignedDoctor('');
    setAssignedAdvisor('');
    setShowAdvisorDropdown(true);
  };

  const applyWellnessAdvisor = (doctorName, advisors, guestIdForAlert) => {
    const trimmed = (doctorName || '').trim();
    if (!trimmed) {
      setAssignedDoctor('');
      setAssignedAdvisor('');
      setShowAdvisorDropdown(true);
      return false;
    }

    if (advisors.length > 0 && !advisors.some((wa) => wa.name === trimmed)) {
      clearStaleWellnessAdvisor(trimmed, guestIdForAlert);
      return false;
    }

    setAssignedDoctor(trimmed);
    setAssignedAdvisor(trimmed);
    setShowAdvisorDropdown(false);
    return true;
  };

  // Re-validate advisor when advisor list loads (e.g. deleted doctor still on saved schedule / CRM)
  useEffect(() => {
    if (!assignedDoctor || wellnessAdvisors.length === 0 || showAdvisorDropdown) return;
    if (!wellnessAdvisors.some((wa) => wa.name === assignedDoctor)) {
      clearStaleWellnessAdvisor(assignedDoctor, guestId);
    }
  }, [wellnessAdvisors, assignedDoctor, showAdvisorDropdown, guestId]);

  const handlePackageChangeOnly = (event, newValue) => {
    if (!guestId) return;

    if (!newValue) {
      setShowPackageDropdown(false);
      return;
    }

    const packageName = newValue.name || newValue.packageName;
    const recordId = newValue?.id || newValue?.recordId || newValue?.record_id;
    const currentNights = nightsNumber;

    setSelectedPackage(newValue);
    setAssignedPackage(newValue);
    setShowPackageDropdown(false);

    notifyAIMSPackageChange(packageName, guestId, externalBookingId);

    saveGuestPackageChange(
      packageName,
      guestId,
      String(recordId || ''),
      currentNights,
      { preserveNights: true },
    );

    setSchedule(null);
    setGeneralInstruction('');
    setTreatmentInstructions({});

    console.log(`✅ Package changed to ${packageName} (nights unchanged at ${currentNights}N)`);
    setTimeout(() => {
      alert(
        `Package updated to "${packageName}".\n\n` +
          'Nights and dates are unchanged. Please generate a new schedule.',
      );
    }, 100);
  };

  const shouldPreserveStayOnPackageChange = () => (
    Boolean(guestId && startDate && (packageLocked || schedule || guestArrivalDate))
  );

  const handlePackageSelect = (event, newValue) => {
    if (packageLocked) return;

    if (shouldPreserveStayOnPackageChange()) {
      if (newValue) {
        handlePackageChangeOnly(event, newValue);
      } else {
        setSelectedPackage(null);
        setAssignedPackage(null);
        setSchedule(null);
      }
      return;
    }

    const previousPackage = selectedPackage;
    setSelectedPackage(newValue);

    // Auto-populate nights since packages have fixed duration
    if (newValue) {
      let nightsNum = newValue.nightsNumber || parseInt(newValue.nights.replace('N', ''));
      const stayNights = computeStayNights(
        guestArrivalDate,
        getCheckOutISO({ checkOut: guestDepartureDate, checkout: guestDepartureDate, departureDate: guestDepartureDate })
      );
      nightsNum = capNightsToGuestStay(nightsNum, stayNights, `Package "${newValue.name}"`);
      setNightsNumber(nightsNum);
      setSelectedNights(`${nightsNum}N`);
      console.log(`✅ Selected package: ${newValue.name} (${nightsNum}N)`);

      // Notify AIIMS of package change if guest is selected
      const guestIdValue = guestId;
      const bookingIdValue = externalBookingId;
      if (guestIdValue && newValue !== previousPackage) {
        console.log('🔍 AIIMS Package Change Debug:', {
          packageName: newValue.name || newValue.packageName,
          guestId: guestIdValue,
          externalBookingId: bookingIdValue
        });
        notifyAIMSPackageChange(newValue.name || newValue.packageName, guestIdValue, bookingIdValue);
        
        // Also save package change to our database
        const recordId = newValue?.id || newValue?.recordId;
        const nightsNum = newValue?.nightsNumber || parseInt(newValue?.nights?.replace('N', '')) || 0;
        saveGuestPackageChange(newValue.name || newValue.packageName, guestIdValue, String(recordId || ''), nightsNum);
      }
    } else {
      setSelectedNights('');
      setNightsNumber(0);
    }

    setSchedule(null); // Clear previous schedule
    setGeneralInstruction(''); // Clear general instruction
    setTreatmentInstructions({}); // Clear treatment instructions
  };

  // Load saved schedule from database when guest is selected
  const loadSavedSchedule = async (
    guestIdToLoad,
    preserveAssignedPackage = false,
    guestArrivalISO = null,
  ) => {
    if (!guestIdToLoad) return;
    scheduleLoadGuestRef.current = guestIdToLoad;
    const stillThisGuest = () => scheduleLoadGuestRef.current === guestIdToLoad;
    
    try {
      console.log(`Loading saved schedule for guest: ${guestIdToLoad}`);
      
      const response = await apiGet(`/api/package-schedules/guest/${encodeURIComponent(guestIdToLoad)}`);
      if (!stillThisGuest()) return;
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log('No saved schedule found for this guest');
          setGeneralInstruction('');
          setTreatmentInstructions({});
          return;
        }
        throw new Error('Failed to load saved schedule');
      }
      
      const savedSchedule = await response.json();
      if (!stillThisGuest()) return;
      
      if (!savedSchedule) {
        console.log('No saved schedule found for this guest');
        setGeneralInstruction('');
        setTreatmentInstructions({});
        return;
      }
      
      console.log('Loaded saved schedule:', savedSchedule);

      const savedStart = toISO(savedSchedule.start_date);
      const arrivalStart = guestArrivalISO ? toISO(guestArrivalISO) : null;
      // Prefer live guest check-in over stale saved start_date (e.g. CRM arrival date changed)
      const effectiveStartDate = arrivalStart || savedStart;
      const datesAdjusted = Boolean(
        arrivalStart && savedStart && arrivalStart !== savedStart,
      );

      if (datesAdjusted) {
        console.warn(
          `Guest arrival ${arrivalStart} differs from saved schedule start ${savedStart} — adjusting day dates`,
        );
      }
      
      // Transform saved schedule back to the format expected by the UI
      const scheduleByDay = {};
      const restoredStandaloneApprovals = {}; // Restore standalone approvals
      const restoredUpgradeApprovals = {}; // Restore upgrade approvals
      
      savedSchedule.items.forEach(item => {
        if (!scheduleByDay[item.day_number]) {
          scheduleByDay[item.day_number] = {
            day: item.day_number,
            date: dayDateFromStart(effectiveStartDate, item.day_number),
            treatments: []
          };
        }
        
        // Use the deterministic ID directly from database (no longer need "saved_" prefix)
        const inclusionId = item.id;
        
        scheduleByDay[item.day_number].treatments.push({
          inclusionId: inclusionId,
          name: item.treatment_name,
          type: item.treatment_type,
          tier_level: item.tier_level,
          duration: item.duration,
          prepTime: item.prep_time,
          cleanupTime: item.cleanup_time,
          isDailyInclusion: false,
          isManuallyAdded: item.is_standalone || false, // RESTORE standalone flag
          isUpgraded: item.is_upgraded || false, // RESTORE upgrade flag
          order: item.order
        });
        
        // RESTORE standalone approval data
        if (item.is_standalone && item.standalone_billing_status) {
          restoredStandaloneApprovals[inclusionId] = {
            billing_status: item.standalone_billing_status,
            discount_amount: item.standalone_discount_amount || 0,
            reason: item.standalone_reason || '',
            approved_by: item.standalone_approved_by || 'Unknown'
          };
        }
        
        // RESTORE upgrade approval data
        if (item.is_upgraded && item.upgrade_billing_status) {
          restoredUpgradeApprovals[inclusionId] = {
            original_tier_level: item.original_tier_level,
            upgrade_amount: item.upgrade_amount || 0,  // CRITICAL: Restore pre-calculated amount
            upgrade_status: item.upgrade_billing_status,
            upgrade_discount_amount: item.upgrade_discount_amount || 0,
            upgrade_reason: item.upgrade_reason || '',
            approved_by: item.upgrade_approved_by || 'Unknown'
          };
        }
      });
      
      const scheduleData = {
        schedule: Object.values(scheduleByDay).sort((a, b) => a.day - b.day)
      };

      const maxDayNumber = Math.max(...savedSchedule.items.map(item => item.day_number));
      const endDateStr = dayDateFromStart(effectiveStartDate, maxDayNumber);

      let mergedScheduleData = scheduleData;
      try {
        console.log(`Fetching existing bookings from ${effectiveStartDate} to ${endDateStr} (${maxDayNumber} days in schedule)`);
        const bookings = await fetchExistingBookings(guestIdToLoad, effectiveStartDate, endDateStr);
        if (!stillThisGuest()) return;
        mergedScheduleData = mergeOrphanBookingsIntoSchedule(scheduleData, bookings, guestIdToLoad);
        setExistingBookings(bookings);

        const bookingResultsData = buildBookingResultsFromSchedule(mergedScheduleData, bookings);
        setBookingResults(bookingResultsData);

        console.log(`Schedule status: ${bookingResultsData.booked_count} booked, ${bookingResultsData.failed_count} not yet booked`);
      } catch (bookingsError) {
        console.warn('Failed to fetch existing bookings:', bookingsError);
      }

      setSchedule(mergedScheduleData);
      setStartDate(effectiveStartDate);
      if (arrivalStart) {
        setGuestArrivalDate(arrivalStart);
      }
      const savedDoctor = savedSchedule.assigned_doctor || '';
      if (savedDoctor) {
        applyWellnessAdvisor(savedDoctor, wellnessAdvisors, guestIdToLoad);
      } else {
        setAssignedDoctor('');
        setAssignedAdvisor('');
      }
      if (!stillThisGuest()) return;
      setGeneralInstruction(savedSchedule.general_instruction || '');
      setTherapistGenderPreference(savedSchedule.therapist_gender_preference || '');
      setTherapistLevel(savedSchedule.therapist_level || '');
      if (savedSchedule.room_preference) {
        const roomObj = rooms.find((r) => r.name === savedSchedule.room_preference);
        setRoomPreference(roomObj || null);
      }
      saveBookingPrefsToStorage(guestIdToLoad, {
        therapistGenderPreference: savedSchedule.therapist_gender_preference || '',
        therapistLevel: savedSchedule.therapist_level || '',
        roomPreferenceName: savedSchedule.room_preference || null,
      });
      setStandaloneApprovals(restoredStandaloneApprovals); // RESTORE standalone approvals
      setUpgradeApprovals(restoredUpgradeApprovals); // RESTORE upgrade approvals
      // Reset booking attempt flag - this is a loaded schedule, not a booking result
      setHasAttemptedBooking(false);
      
      // Restore package from saved schedule only when guest has no CRM-assigned package
      if (savedSchedule.package_name && !preserveAssignedPackage) {
        const savedPackageName = savedSchedule.package_name;
        console.log(`🔍 Looking for saved package: ${savedPackageName}`);
        
        // Find matching package in packages by name
        const matchedPackage = packages.find(pkg => 
          pkg.name === savedPackageName || pkg.programName === savedPackageName
        );
        
        if (matchedPackage) {
          console.log(`✅ Restored package from saved schedule: ${savedPackageName}`);
          setSelectedPackage(matchedPackage);
          // Also set nights from saved schedule
          if (savedSchedule.nights) {
            setSelectedNights(savedSchedule.nights);
            const nightsNum = parseInt(savedSchedule.nights.replace('N', ''));
            setNightsNumber(nightsNum);
          }
        } else {
          console.warn(`⚠️ Could not find package '${savedPackageName}' in local packages`);
        }
      }
      
      console.log('✅ Successfully loaded saved schedule');
      if (datesAdjusted) {
        try {
          const pkgInfo = savedSchedule.package_name
            ? { name: savedSchedule.package_name, programName: savedSchedule.package_name }
            : null;
          await saveScheduleToDatabase(
            mergedScheduleData,
            pkgInfo,
            savedSchedule.package_record_id,
            restoredUpgradeApprovals,
            restoredStandaloneApprovals,
            effectiveStartDate,
          );
          console.log('✅ Persisted adjusted start date to match guest arrival');
        } catch (persistErr) {
          console.warn('Could not persist adjusted start date:', persistErr);
        }
        alert(
          `Previously saved schedule loaded.\n\n` +
            `Guest check-in is ${arrivalStart} but the saved schedule started ${savedStart}. ` +
            `Day dates were adjusted to match the guest's arrival — you can book or regenerate if needed.`,
        );
      } else {
        alert('Previously saved schedule loaded! You can modify and book it.');
      }
      
    } catch (error) {
      console.error('Error loading saved schedule:', error);
      // Non-blocking - just log the error
    }
  };

  // Save schedule to database for persistence
  const saveScheduleToDatabase = async (
    scheduleData,
    packageInfo,
    recordId,
    customUpgradeApprovals = null,
    customStandaloneApprovals = null,
    startDateOverride = null,
  ) => {
    // Use custom approvals if provided, otherwise fall back to state
    const upgradeApprovalsToUse = customUpgradeApprovals !== null ? customUpgradeApprovals : upgradeApprovals;
    const standaloneApprovalsToUse = customStandaloneApprovals !== null ? customStandaloneApprovals : standaloneApprovals;
    
    console.log('💾 [SAVE] Saving schedule to database...');
    console.log('💾 [SAVE] Using upgradeApprovals:', upgradeApprovalsToUse);
    console.log('💾 [SAVE] Using standaloneApprovals:', standaloneApprovalsToUse);
    
    // Transform schedule data to match backend schema
    const items = [];
    if (scheduleData.schedule) {
      scheduleData.schedule.forEach((day) => {
        day.treatments.forEach((treatment) => {
          const inclusionId = treatment.inclusionId;
          const approval = standaloneApprovalsToUse[inclusionId];
          const upgradeApproval = upgradeApprovalsToUse[inclusionId];
          
          console.log(`💾 [SAVE] Treatment: ${treatment.name}, inclusionId: ${inclusionId}`);
          console.log(`💾 [SAVE]   - isUpgraded flag: ${treatment.isUpgraded}`);
          console.log(`💾 [SAVE]   - upgradeApproval found: ${upgradeApproval ? 'YES' : 'NO'}`);
          if (upgradeApproval) {
            console.log(`💾 [SAVE]   - upgradeApproval data:`, upgradeApproval);
          }
          
          // Use the deterministic ID (same as inclusionId)
          const isStandalone = treatment.isManuallyAdded || false;
          console.log(`💾 [SAVE] Day ${day.day}, Treatment: ${treatment.name}`);
          console.log(`💾 [SAVE]   - isManuallyAdded: ${treatment.isManuallyAdded}`);
          console.log(`💾 [SAVE]   - is_standalone (sending to backend): ${isStandalone}`);
          console.log(`💾 [SAVE]   - isDailyInclusion: ${treatment.isDailyInclusion}`);
          
          items.push({
            id: inclusionId,  // CRITICAL: Send deterministic ID to backend
            day_number: day.day,
            treatment_name: treatment.name,
            duration: treatment.duration || 50,
            prep_time: treatment.prepTime || 10,
            cleanup_time: treatment.cleanupTime || 15,
            treatment_type: treatment.type,
            tier_level: treatment.tier_level,
            order: treatment.order,
            treatment_instruction: treatmentInstructions[inclusionId] || null,
            // Standalone tracking - CRITICAL for billing
            is_standalone: isStandalone,
            standalone_billing_status: approval ? approval.billing_status : null,
            standalone_discount_amount: approval ? approval.discount_amount : 0,
            standalone_reason: approval ? approval.reason : null,
            standalone_approved_by: approval ? approval.approved_by : null,
            // Upgrade tracking - CRITICAL for billing
            is_upgraded: treatment.isUpgraded || false,
            original_tier_level: upgradeApproval ? upgradeApproval.original_tier_level : null,
            upgrade_amount: upgradeApproval ? upgradeApproval.upgrade_amount : 0,  // CRITICAL: Pre-calculated approved amount
            upgrade_billing_status: upgradeApproval ? upgradeApproval.upgrade_status : null,
            upgrade_discount_amount: upgradeApproval ? upgradeApproval.upgrade_discount_amount : 0,
            upgrade_reason: upgradeApproval ? upgradeApproval.upgrade_reason : null,
            upgrade_approved_by: upgradeApproval ? upgradeApproval.approved_by : null
          });
        });
      });
    }
    
    const savePayload = {
      guest_id: guestId,
      guest_name: guestName,
      package_name: packageInfo?.name || packageInfo?.programName || 'Unknown Package',
      package_record_id: String(recordId), // Convert to string to match API expectation
      nights: selectedNights,
      start_date: startDateOverride || startDate,
      assigned_doctor: assignedDoctor,
      general_instruction: generalInstruction || null,
      therapist_gender_preference: therapistGenderPreference || null,
      therapist_level: therapistLevel || null,
      room_preference: roomPreference?.name || null,
      items: items
    };
    
    console.log('Save payload:', savePayload);
    
    const response = await apiPost('/api/package-schedules/', savePayload);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to save schedule');
    }
    
    const savedSchedule = await response.json();
    console.log('💾 [SAVE] Schedule saved to database:', savedSchedule);
    console.log('💾 [SAVE] ✅ Using deterministic IDs - no remapping needed!');
    
    return savedSchedule;
  };

  // Fetch existing bookings for a guest across a date range
  const fetchExistingBookings = async (guestIdToCheck, startDateStr, endDateStr) => {
    try {
      console.log(`Fetching existing bookings for guest ${guestIdToCheck} from ${startDateStr} to ${endDateStr}`);
      
      // Calculate all dates in range (local calendar dates — avoid UTC shift)
      const allBookings = [];
      let currentDate = startDateStr;
      while (currentDate <= endDateStr) {
        const dateStr = currentDate;
        try {
          const response = await apiGet(`/api/bookings/${dateStr}`);
          if (response.ok) {
            const dayBookings = await response.json();
            // Filter for this guest and package services
            const guestBookings = dayBookings.filter(
              b => (b.guestId === guestIdToCheck || b.guest_id === guestIdToCheck)
            );
            
            if (guestBookings.length > 0) {
              console.log(`   ${dateStr}: Found ${guestBookings.length} bookings - ${guestBookings.map(b => b.treatment).join(', ')}`);
            }
            
            allBookings.push(...guestBookings.map(b => ({
              ...b,
              date: dateStr,
              treatment: b.treatment,
              bookingId: b.bookingId || b.id,
              // Ensure both field name variants are present for EditBookingModal
              guestId: b.guestId || b.guest_id,
              guest_id: b.guest_id || b.guestId,
              guestName: b.guestName || b.guest_name,
              guest_name: b.guest_name || b.guestName,
              guestRoom: b.guestRoom || b.guest_room,
              guest_room: b.guest_room || b.guestRoom
            })));
          }
        } catch (err) {
          console.warn(`Failed to fetch bookings for ${dateStr}:`, err);
        }
        currentDate = addDaysISO(currentDate, 1);
      }
      
      console.log(`✅ Found ${allBookings.length} total existing bookings for guest ${guestIdToCheck}`);
      if (allBookings.length > 0) {
        console.log('   Bookings:', allBookings.map(b => `${b.treatment} (${b.date})`).join(', '));
      }
      return allBookings;
    } catch (error) {
      console.error('Error fetching existing bookings:', error);
      return [];
    }
  };

  const handleGenerateSchedule = async () => {
    console.log('Generate Schedule clicked');
    console.log('Selected Package:', selectedPackage);
    console.log('Assigned Package:', assignedPackage);
    console.log('Selected Nights:', selectedNights);
    console.log('Nights Number:', nightsNumber);
    console.log('Guest Name:', guestName);
    console.log('Guest ID:', guestId);
    console.log('Wellness Advisor:', assignedDoctor);
    console.log('Start Date:', startDate);

    // Use either selectedPackage or assignedPackage
    const packageToUse = selectedPackage || assignedPackage;

    // Validate all required fields
    if (!packageToUse || !selectedNights || !guestName || !guestId || !assignedDoctor || !startDate) {
      const missingFields = [];
      if (!packageToUse) missingFields.push('Package');
      if (!selectedNights) missingFields.push('Nights');
      if (!guestName) missingFields.push('Guest Name');
      if (!guestId) missingFields.push('Guest ID');
      if (!assignedDoctor) missingFields.push('Wellness Advisor');
      if (!startDate) missingFields.push('Start Date');
      
      const errorMsg = `Please fill in all required fields: ${missingFields.join(', ')}`;
      console.error(errorMsg);
      setScheduleError(errorMsg);
      return;
    }

    // Ensure selectedNights is in the correct format (e.g., "14N")
    let nightsParam = selectedNights;
    if (!nightsParam || nightsParam.trim() === '') {
      // Fallback to nightsNumber if selectedNights is empty
      if (nightsNumber > 0) {
        nightsParam = `${nightsNumber}N`;
        console.log('Using nightsNumber fallback:', nightsParam);
      } else {
        setScheduleError('Number of nights is not set properly');
        return;
      }
    }

    try {
      setScheduleLoading(true);
      setScheduleError(null);
      
      // Generate schedule from local retreat package
      console.log('📦 Generating schedule for retreat package');
      console.log('📦 Package object:', JSON.stringify(packageToUse, null, 2));
      console.log('📦 Package name:', packageToUse.name);
      console.log('📦 Package nights:', packageToUse.nights);
      
      // Get package ID - local packages use 'id', some may have 'recordId'
      const packageId = packageToUse.id || packageToUse.recordId;
      console.log('📦 Package ID to use:', packageId);
      console.log('📦 Package ID type:', typeof packageId);
      
      if (!packageId) {
        console.error('❌ Package structure:', Object.keys(packageToUse));
        throw new Error(`Package does not have a valid ID. Available fields: ${Object.keys(packageToUse).join(', ')}`);
      }
      
      const params = new URLSearchParams({
        start_date: startDate,
        guest_name: guestName,
        guest_id: guestId,
        assigned_doctor: assignedDoctor
      });
      
      const apiUrl = `/api/retreat-packages/generate-schedule/${packageId}?${params.toString()}`;
      console.log('Making API call to:', apiUrl);
      console.log('Params breakdown:', {
        package_id: packageId,
        start_date: startDate,
        guest_name: guestName,
        guest_id: guestId,
        assigned_doctor: assignedDoctor
      });
      
      const response = await apiPost(apiUrl, {});

      console.log('API Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ API Error:', errorData);
        console.error('❌ Response status:', response.status);
        console.error('❌ Response statusText:', response.statusText);
        
        // Show detailed error to user
        const errorMessage = errorData.detail || 'Failed to generate schedule';
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Schedule generated:', data);
      
      // DEBUG: Check tier_level in schedule data
      if (data.schedule && data.schedule[0]) {
        const firstDayTreatments = data.schedule[0].treatments.map(t => ({
          name: t.name,
          tier: t.tier_level
        }));
        console.log('First day treatments with tiers:', JSON.stringify(firstDayTreatments, null, 2));
      }
      
      // Add deterministic IDs to all treatments
      // ALWAYS generate deterministic IDs (overwrite any auto-generated ones from backend)
      if (data.schedule) {
        data.schedule.forEach(day => {
          if (day.treatments) {
            day.treatments.forEach(treatment => {
              // Generate deterministic ID for ALL treatments
              treatment.inclusionId = generateDeterministicId(
                guestId,
                day.day,
                treatment.name,
                treatment.order
              );
              console.log(`🆔 Generated ID: ${treatment.inclusionId} for ${treatment.name}`);
            });
          }
        });
      }
      
      setSchedule(data);
      // Reset instructions when new schedule is generated
      setTreatmentInstructions({});
      // Reset booking attempt flag - this is a new schedule, not a booking result
      setHasAttemptedBooking(false);
      
      // Fetch existing bookings for this guest
      try {
        const nightsNum = parseInt(nightsParam.replace('N', ''));
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + nightsNum);
        const endDateStr = endDate.toISOString().split('T')[0];
        
        const bookings = await fetchExistingBookings(guestId, startDate, endDateStr);
        setExistingBookings(bookings);
        
        // Compare schedule with existing bookings (failures shown only after a book attempt)
        console.log(`Found ${bookings.length} existing bookings for this guest`);
        
        setBookingResults({
          success: bookings.length > 0,
          bookings: bookings.map(b => ({
            treatmentName: b.treatment,
            date: b.date,
            startTime: b.startTime || b.start_time,
            endTime: b.endTime || b.end_time,
            therapist: b.therapist,
            room: b.room,
            bookingId: b.bookingId,
            duration: b.duration,  // Custom duration
            custom_duration: b.custom_duration,  // Custom duration flag
            isExisting: true // Mark as pre-existing
          })),
          booked_count: bookings.length,
          failures: [],
          failed_count: 0,
          summary: {
            total: data.schedule.reduce((sum, day) => sum + day.treatments.length, 0),
            booked: bookings.length,
            failed: 0
          }
        });
        
        console.log(`Schedule status: ${bookings.length} booked`);
      } catch (bookingsError) {
        console.warn('Failed to fetch existing bookings:', bookingsError);
        // Non-blocking - continue with schedule generation
      }
      
      // Save schedule to database for persistence
      try {
        const recordId = packageToUse?.id || packageToUse?.recordId;
        await saveScheduleToDatabase(data, packageToUse, recordId);
      } catch (saveError) {
        console.warn('Failed to save schedule to database:', saveError);
        // Non-blocking - schedule still works even if save fails
      }
    } catch (err) {
      console.error('Error generating schedule:', err);
      setScheduleError(err.message);
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleTreatmentInstructionChange = (inclusionId, value) => {
    setTreatmentInstructions(prev => ({
      ...prev,
      [inclusionId]: value
    }));
  };

  const handleBookAllAppointments = async () => {
    if (!schedule) {
      alert('Please generate a schedule first');
      return;
    }

    // Validate: Check if any scheduled date is beyond the guest's checkout date
    if (guestDepartureDate && schedule.schedule) {
      const departureDateISO = getCheckOutISO({ checkOut: guestDepartureDate, checkout: guestDepartureDate, departureDate: guestDepartureDate });
      
      if (departureDateISO) {
        const daysAfterCheckout = schedule.schedule.filter(day => day.date > departureDateISO);
        
        if (daysAfterCheckout.length > 0) {
          const checkoutFormatted = new Date(departureDateISO).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
          const invalidDays = daysAfterCheckout.map(d => `Day ${d.day} (${d.date})`).join(', ');
          
          alert(`Cannot book appointments.\n\nThe following days are after the guest's checkout date (${checkoutFormatted}):\n${invalidDays}\n\nPlease remove these days from the schedule or extend the guest's stay in the CRM system first.`);
          return;
        }
      }
    }

    try {
      setBookingInProgress(true);
      // Clear any previous outcome so the modal opens straight into the
      // in-progress state without a flash of an old success/error panel.
      setBookingOutcome(null);
      setBookingChunkProgress(null);
      // NOTE: Do NOT set hasAttemptedBooking(true) here. It is set only after the
      // backend response arrives so the per-treatment "❌ Failed: Not yet booked"
      // badges and "Failed: N" summary never flash while the request is in flight.
      // Keep existing bookings, but clear stale "Not yet booked" failure entries
      // that were populated when the schedule was first loaded.
      const existingResults = bookingResults?.bookings?.filter(b => b.isExisting) || [];
      setBookingResults(prev => ({
        ...prev,
        bookings: existingResults,
        failures: [],
        failed_count: 0,
        summary: prev?.summary ? { ...prev.summary, failed: 0 } : prev?.summary
      }));
      
      console.log('Starting package booking...');
      console.log('Schedule:', schedule);
      console.log('Existing bookings:', existingBookings);
      console.log('Preferences:', {
        therapistGenderPreference,
        therapistLevel,
        roomPreference: roomPreference?.name
      });

      // CRITICAL: Save schedule to database before booking (includes manually added treatments)
      try {
        const packageToUse = selectedPackage || assignedPackage;
        const recordId = packageToUse?.recordId || packageToUse?.programId || packageToUse?.programRecordId || packageToUse?.id;
        
        console.log('💾 Saving schedule before booking (includes standalone treatments)...');
        await saveScheduleToDatabase(schedule, packageToUse, recordId);
        console.log('✅ Schedule saved successfully');
      } catch (saveError) {
        console.error('⚠️ Failed to save schedule before booking:', saveError);
        // Continue with booking even if save fails - non-blocking
      }

      // Filter out already-booked treatments from the schedule
      console.log('🔍 Filtering schedule against existing bookings:');
      console.log(`   Schedule dates: ${schedule.schedule.map(d => d.date).join(', ')}`);
      console.log(`   Existing bookings (${existingBookings.length}):`, existingBookings.map(b => `${b.treatment} on ${b.date} (${b.status || 'N/A'})`));
      
      const filteredSchedule = schedule.schedule.map(day => ({
        ...day,
        treatments: day.treatments.filter(treatment => {
          // Check if this treatment is already booked on this date
          const isAlreadyBooked = existingBookings.some(
            booking => {
              const dateMatches = booking.date === day.date;
              const bookedName = booking.treatment || booking.treatmentName;
              const entitlement = booking.original_treatment || booking.originalTreatment;
              const treatmentMatches = bookedName === treatment.name || entitlement === treatment.name;
              const notCancelled = booking.status !== 'CANCELLED' && booking.status !== 'CANCELED';
              
              if (dateMatches && treatmentMatches && notCancelled) {
                console.log(`✅ Match found: "${treatment.name}" on ${day.date} (status: ${booking.status || 'CONFIRMED'})`);
                return true;
              }
              
              // Cancelled bookings don't count as "booked"
              if (dateMatches && treatmentMatches && !notCancelled) {
                console.log(`🔄 Found cancelled booking: "${treatment.name}" on ${day.date} - allowing re-booking`);
                return false;
              }
              
              // Debug non-matches
              if (booking.treatment === treatment.name && notCancelled) {
                console.log(`⚠️  Treatment matches but date doesn't: "${treatment.name}" (schedule: ${day.date}, booking: ${booking.date})`);
              }
              
              return false;
            }
          );
          if (isAlreadyBooked) {
            console.log(`⏭️  Skipping already-booked: ${treatment.name} on ${day.date}`);
          }
          return !isAlreadyBooked;
        })
      })).filter(day => day.treatments.length > 0); // Remove days with no treatments to book

      if (filteredSchedule.length === 0) {
        alert('All treatments in this schedule are already booked!');
        setBookingInProgress(false);
        return;
      }

      console.log(`Booking ${filteredSchedule.reduce((sum, day) => sum + day.treatments.length, 0)} unbooked treatments (skipped ${existingBookings.length} already-booked)`);


      // Validate guest gender for gender-specific treatments
      if (!guestGender || guestGender.trim() === '') {
        const proceed = window.confirm(
          '⚠️ Guest gender is not specified.\n\n' +
          'Some treatments may require same-gender staff assignment.\n' +
          'Without gender information, these treatments might not be bookable.\n\n' +
          'Do you want to proceed anyway?'
        );
        if (!proceed) {
          setBookingInProgress(false);
          return; // User cancelled
        }
      }

      const requestBody = {
        guest_name: guestName,
        guest_id: guestId,
        external_booking_id: externalBookingId,  // External resort booking ID (BKG format)
        guest_gender: guestGender,  // For automatic gender matching with services
        guest_room: guestRoom, // Guest's room number for validation
        assigned_doctor: assignedDoctor,
        schedule: filteredSchedule, // May be replaced per chunk below
        general_instruction: generalInstruction,
        treatment_instructions: treatmentInstructions,
        therapist_gender_preference: therapistGenderPreference,
        therapist_level: therapistLevel,
        room_preference: roomPreference?.name || null,
        guest_eta: guestCheckinETA || '', // Day 1 scheduling; backend defaults to 1:00 PM if empty
        standalone_approvals: standaloneApprovals, // Include approval data for manually added treatments
        upgrade_approvals: upgradeApprovals // Include upgrade approval data
      };

      const fullLastDayNumber = Math.max(...filteredSchedule.map((d) => d.day));
      const treatmentGrandTotal = filteredSchedule.reduce((sum, day) => sum + day.treatments.length, 0);

      // Long schedules: split into multi-day chunks so each HTTP request finishes within
      // client/proxy timeouts (~30s default fetch). Backend seeds time-diversity from DB per day.
      const SCHEDULE_CHUNK_DAYS = 2;
      const BOOK_SCHEDULE_TIMEOUT_MS = 120000;
      const scheduleChunks = [];
      for (let i = 0; i < filteredSchedule.length; i += SCHEDULE_CHUNK_DAYS) {
        scheduleChunks.push(filteredSchedule.slice(i, i + SCHEDULE_CHUNK_DAYS));
      }

      console.log('📤 BOOKING REQUEST BODY (template):', JSON.stringify({ ...requestBody, schedule: `[${scheduleChunks.length} chunk(s)]` }, null, 2));

      let aggBookings = [];
      let aggFailures = [];
      let aggBooked = 0;
      let aggFailed = 0;

      for (let ci = 0; ci < scheduleChunks.length; ci++) {
        const chunk = scheduleChunks[ci];
        if (scheduleChunks.length > 1) {
          const dFirst = chunk[0].day;
          const dLast = chunk[chunk.length - 1].day;
          setBookingChunkProgress({
            current: ci + 1,
            total: scheduleChunks.length,
            label: dFirst === dLast ? `Day ${dFirst}` : `Days ${dFirst}–${dLast}`
          });
        }

        const chunkBody = {
          ...requestBody,
          schedule: chunk,
          last_day_number_in_package: fullLastDayNumber
        };

        const response = await apiPost('/api/packages/book-schedule', chunkBody, {
          timeout: BOOK_SCHEDULE_TIMEOUT_MS
        });

        console.log(`Chunk ${ci + 1}/${scheduleChunks.length} status:`, response.status);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Scheduling batch ${ci + 1} of ${scheduleChunks.length} failed`);
        }

        const chunkResults = await response.json();
        console.log(`Chunk ${ci + 1} results:`, chunkResults);

        aggBookings.push(...(chunkResults.bookings || []));
        aggFailures.push(...(chunkResults.failures || []));
        aggBooked += chunkResults.booked_count || 0;
        aggFailed += chunkResults.failed_count || 0;

        if (chunkResults.bookings && chunkResults.bookings.length > 0) {
          const newBookings = chunkResults.bookings.map((b) => ({
            treatment: b.treatmentName,
            date: b.date,
            startTime: b.startTime,
            endTime: b.endTime,
            therapist: b.therapist,
            room: b.room,
            bookingId: b.bookingId,
            duration: b.duration,
            custom_duration: b.custom_duration
          }));
          setExistingBookings((prev) => [...prev, ...newBookings]);
        }
      }

      setBookingChunkProgress(null);

      // Reconcile UI with DB: merge any booked add-ons missing from saved schedule items
      let mergedSchedule = schedule;
      let refreshedBookings = existingBookings;
      try {
        const maxDay = Math.max(...schedule.schedule.map((d) => d.day));
        const endDateStr = dayDateFromStart(startDate, maxDay);
        refreshedBookings = await fetchExistingBookings(guestId, startDate, endDateStr);
        setExistingBookings(refreshedBookings);
        const treatmentsBefore = schedule.schedule.reduce((sum, d) => sum + d.treatments.length, 0);
        mergedSchedule = mergeOrphanBookingsIntoSchedule(schedule, refreshedBookings, guestId);
        setSchedule(mergedSchedule);

        const treatmentsAfter = mergedSchedule.schedule.reduce((sum, d) => sum + d.treatments.length, 0);
        if (treatmentsAfter > treatmentsBefore) {
          const packageToUse = selectedPackage || assignedPackage;
          const recordId = packageToUse?.recordId || packageToUse?.programId || packageToUse?.programRecordId || packageToUse?.id;
          if (packageToUse && recordId) {
            await saveScheduleToDatabase(mergedSchedule, packageToUse, recordId);
            console.log('✅ Persisted synced add-on treatments to package schedule');
          }
        }
      } catch (syncErr) {
        console.warn('Post-book schedule sync failed:', syncErr);
      }

      const results = {
        success: aggBooked > 0,
        bookings: aggBookings,
        failures: aggFailures,
        booked_count: aggBooked,
        failed_count: aggFailed,
        summary: {
          total: treatmentGrandTotal,
          booked: aggBooked,
          failed: aggFailed,
          success_rate:
            treatmentGrandTotal > 0
              ? `${((aggBooked / treatmentGrandTotal) * 100).toFixed(1)}%`
              : '0%'
        }
      };

      console.log('Booking results (aggregated):', results);

      const mergedResults = buildBookingResultsFromSchedule(
        mergedSchedule,
        refreshedBookings,
        aggFailures
      );

      setBookingResults(mergedResults);
      // Now that real results have arrived, allow the per-treatment failure badges
      // and the summary box to render.
      setHasAttemptedBooking(true);

      const totalAttempted = results.summary?.total || 0;
      const alreadyBooked = existingBookings.length;
      const failedCount = results.failed_count || 0;
      const newlyBooked = results.booked_count || 0;

      // Show outcome inside the same modal (replaces native alert popups).
      setBookingOutcome({
        type: failedCount === 0 ? 'success' : 'partial',
        bookedCount: newlyBooked,
        alreadyBooked,
        failedCount,
        totalAttempted,
        successRate: results.summary?.success_rate || (failedCount === 0 ? '100%' : null),
        failures: results.failures || [],
      });

    } catch (err) {
      console.error('Error booking appointments:', err);
      // Mark attempt so any remaining failure UI can render after the modal closes.
      setHasAttemptedBooking(true);
      setBookingOutcome({
        type: 'error',
        message: err.message || 'Unknown error',
      });
    } finally {
      setBookingInProgress(false);
      setBookingChunkProgress(null);
    }
  };

  // Dismiss the booking outcome panel (modal closes when both the in-progress
  // flag and the outcome are cleared).
  const handleDismissBookingOutcome = () => setBookingOutcome(null);

  const checkInRoomTherapy = (roomNumber) => {
    // Check if guest's room has in-room therapy capability
    if (roomNumber && allRoomsData && Object.keys(allRoomsData).length > 0) {
      const guestRoomKey = `Room No:${roomNumber}`;
      
      // Find therapy room matching guest's room location
      const matchingRoom = Object.entries(allRoomsData).find(
        ([roomName, roomData]) => roomData.location === guestRoomKey
      );
      
      if (matchingRoom) {
        console.log(`✅ In-room therapy available: ${matchingRoom[0]} for guest in ${roomNumber}`);
        
        // Auto-set room preference to guest's room therapy
        const roomObj = rooms.find(r => r.name === matchingRoom[0]);
        setRoomPreference(roomObj || null);
        setGuestRoomTherapyAvailable(true);
      } else {
        console.log(`ℹ️ No in-room therapy for guest room ${roomNumber}, will use common rooms`);
        setRoomPreference(null);
        setGuestRoomTherapyAvailable(false);
      }
    } else {
      setRoomPreference(null);
      setGuestRoomTherapyAvailable(false);
    }
  };

  const handleGuestSelect = (event, newValue) => {
    if (newValue) {
      const selectedGuestId = newValue.guestId || newValue.value || '';
      scheduleLoadGuestRef.current = selectedGuestId;
      setGeneralInstruction('');
      setTreatmentInstructions({});
      setGuestName(newValue.guestName || newValue.label);
      setGuestId(selectedGuestId);
      
      // Extract external booking ID (BKG format)
      const extBookingId = newValue.bookingId || newValue.booking_id || '';
      setExternalBookingId(extBookingId);
      console.log('External Booking ID:', extBookingId);
      
      // Extract guest gender (for gender-specific treatment matching)
      const gender = normalizeGuestGenderForSelect(newValue.gender || newValue.Gender || '');
      setGuestGender(gender);
      console.log('Guest gender:', gender);
      
      // Extract guest room number
      const guestRoomNumber = newValue.room != null && newValue.room !== '' ? String(newValue.room) : '';
      setGuestRoom(guestRoomNumber);
      
      // Extract guest ETA (Estimated Time of Arrival) and ETD (Estimated Time of Departure)
      const eta = newValue.eta || newValue.ETA || '';
      const etd = newValue.etd || newValue.ETD || '';
      setGuestCheckinETA(eta);
      setGuestCheckoutETD(etd);
      console.log('Guest ETA:', eta, 'ETD:', etd);
      
      // Extract arrival and departure dates (all CRM field variants)
      const checkInDate = getCheckInISO(newValue);
      const departureISO = getCheckOutISO(newValue);
      const stayNights = computeStayNights(checkInDate, departureISO);
      setGuestArrivalDate(checkInDate || '');
      setGuestDepartureDate(departureISO || '');
      console.log('Guest Arrival:', checkInDate, 'Departure:', departureISO, 'Stay nights:', stayNights);
      
      // Check for in-room therapy
      checkInRoomTherapy(guestRoomNumber);

      const guestIdValue = newValue.guestId || newValue.value;
      const storedPrefs = loadBookingPrefsFromStorage(guestIdValue);
      if (storedPrefs) {
        setTherapistGenderPreference(storedPrefs.therapistGenderPreference || '');
        setTherapistLevel(storedPrefs.therapistLevel || '');
        if (storedPrefs.roomPreferenceName) {
          const roomObj = rooms.find((r) => r.name === storedPrefs.roomPreferenceName);
          if (roomObj) setRoomPreference(roomObj);
        }
      }
      
      // Set start date to check-in date
      if (checkInDate) {
        setStartDate(checkInDate);
        console.log('Auto-filled start date:', checkInDate);
      } else {
        console.log('No check-in date found for guest');
      }
      
      // Auto-fill package if assigned in guest data
      const crmPackageName = newValue.program || newValue.package || newValue.packageName;
      const crmNights = newValue.nights || newValue.packageNights;
      if (crmPackageName && crmNights) {
        // Set nights (remove "N" if present for the number field)
        const nightsStr = String(crmNights);
        let nightsNum = parseInt(nightsStr.replace(/[^\d]/g, '')) || 0;
        if (stayNights) {
          nightsNum = capNightsToGuestStay(nightsNum, stayNights, `CRM package "${crmPackageName}"`);
        }
        setNightsNumber(nightsNum);
        setSelectedNights(`${nightsNum}N`);
        
        console.log('Auto-filled nights:', {
          nightsStr,
          nightsNum,
          selectedNights: nightsStr.includes('N') ? nightsStr : `${nightsStr}N`
        });
        
        // Try to find matching local package by NAME
        let matchedPackage = null;
        const guestPackageName = String(crmPackageName).toLowerCase().trim();
        
        // Step 1: Try exact match by name AND nights
        matchedPackage = packages.find(p => {
          const pkgNights = parseInt(String(p.nights).replace(/[^\d]/g, '')) || 0;
          const nameMatch = p.name.toLowerCase().trim() === guestPackageName;
          return nameMatch && pkgNights === nightsNum;
        });
        
        if (matchedPackage) {
          console.log('✅ Exact match found:', matchedPackage.name, `(${matchedPackage.nights}N)`);
        } else {
          // Step 2: No exact match - find closest nights FROM BELOW (prefer shorter packages, then add days)
          const sameNamePackages = packages.filter(p => 
            p.name.toLowerCase().trim() === guestPackageName
          );
          
          if (sameNamePackages.length > 0) {
            // Strategy: Prefer packages with FEWER nights than guest needs (easier to add than remove)
            // First, try to find packages with nights <= guest needs
            const packagesBelow = sameNamePackages.filter(p => {
              const pkgNights = parseInt(String(p.nights).replace(/[^\d]/g, '')) || 0;
              return pkgNights <= nightsNum;
            });
            
            if (packagesBelow.length > 0) {
              // Pick the one closest to guest needs (from below)
              matchedPackage = packagesBelow.reduce((closest, pkg) => {
                const pkgNights = parseInt(String(pkg.nights).replace(/[^\d]/g, '')) || 0;
                const closestNights = parseInt(String(closest.nights).replace(/[^\d]/g, '')) || 0;
                return pkgNights > closestNights ? pkg : closest; // Pick largest from below
              });
              
              const matchedNights = parseInt(String(matchedPackage.nights).replace(/[^\d]/g, '')) || 0;
              console.log(`⚠️ No exact match. Using closest from below: ${matchedPackage.name} (${matchedNights}N) for guest's ${nightsNum}N`);
            } else {
              // No packages with fewer nights - must use one with more nights (fallback)
              matchedPackage = sameNamePackages.reduce((closest, pkg) => {
                const pkgNights = parseInt(String(pkg.nights).replace(/[^\d]/g, '')) || 0;
                const closestNights = parseInt(String(closest.nights).replace(/[^\d]/g, '')) || 0;
                return pkgNights < closestNights ? pkg : closest; // Pick smallest from above
              });
              
              const matchedNights = parseInt(String(matchedPackage.nights).replace(/[^\d]/g, '')) || 0;
              console.log(`⚠️ No packages with fewer nights. Using smallest available: ${matchedPackage.name} (${matchedNights}N) for guest's ${nightsNum}N`);
            }
            
            const matchedNights = parseInt(String(matchedPackage.nights).replace(/[^\d]/g, '')) || 0;
            if (matchedNights !== nightsNum) {
              const diff = nightsNum - matchedNights;
              const action = diff > 0 ? 'add' : 'remove';
              setTimeout(() => {
                alert(`📦 Package Match: "${matchedPackage.name}"\n\n` +
                      `Guest needs: ${nightsNum} nights\n` +
                      `Package has: ${matchedNights} nights\n\n` +
                      `✅ Schedule will be generated for ${matchedNights} nights.\n` +
                      `You can ${action} ${Math.abs(diff)} day(s) manually after generation.`);
              }, 100);
            }
          }
        }
        
        if (matchedPackage) {
          // Found a matching package (exact or closest)
          setSelectedPackage(matchedPackage);
          setAssignedPackage(matchedPackage);
          setPackageLocked(true);
          setShowPackageDropdown(false);
          console.log('✅ Using package:', matchedPackage.name, matchedPackage.nights);
        } else {
          // No package with matching name found at all — keep CRM package read-only
          console.warn(`⚠️ No package found with name "${crmPackageName}"`);
          console.log('   Available packages:', packages.map(p => `${p.name} (${p.nights})`).join(', '));
          const nightsLabel = nightsStr.includes('N') ? nightsStr : `${nightsNum}N`;
          setAssignedPackage({ name: crmPackageName, nights: nightsLabel });
          setSelectedPackage(null);
          setPackageLocked(true);
          setShowPackageDropdown(false);

          setTimeout(() => {
            alert(`Package "${crmPackageName}" not found in local packages.\n\n` +
                  `Use Change to select a local package, or create "${crmPackageName}" in Retreat Package Builder.`);
          }, 100);
        }
        
        console.log('Auto-filled package:', crmPackageName, crmNights);
      } else {
        // No package assigned - show dropdown; default nights from actual stay dates
        setAssignedPackage(null);
        setSelectedPackage(null);
        setPackageLocked(false);
        setShowPackageDropdown(true);
        if (stayNights) {
          setNightsNumber(stayNights);
          setSelectedNights(`${stayNights}N`);
          console.log('No CRM package — using stay nights:', stayNights);
        }
        console.log('No package assigned - showing dropdown');
      }
      
      // Auto-fill wellness advisor if assigned (validate against active advisors)
      if (newValue.consultingDoctor) {
        const guestIdValue = newValue.guestId || newValue.value;
        const bookingIdValue = newValue.bookingId || newValue.externalBookingId;
        const applied = applyWellnessAdvisor(newValue.consultingDoctor, wellnessAdvisors, guestIdValue);
        if (applied && guestIdValue) {
          notifyAIMSDoctorAssignment(newValue.consultingDoctor, guestIdValue, bookingIdValue);
        }
        console.log('Auto-filled wellness advisor:', newValue.consultingDoctor);
      } else {
        // No advisor assigned - show dropdown
        setAssignedAdvisor('');
        setAssignedDoctor('');
        setShowAdvisorDropdown(true);
        console.log('No wellness advisor assigned - showing dropdown');
      }
      
      // Load saved schedule if exists (may override advisor from saved schedule)
      loadSavedSchedule(
        newValue.guestId || newValue.value,
        !!(crmPackageName && crmNights),
        checkInDate,
      );
    } else {
      // Clear all fields without reloading the page
      setGuestName('');
      setGuestId('');
      setExternalBookingId('');
      setGuestGender('');
      setGuestRoom('');
      setGuestCheckinETA('');
      setGuestCheckoutETD('');
      setGuestArrivalDate('');
      setGuestDepartureDate('');
      setTherapistGenderPreference('');
      setTherapistLevel('');
      setRoomPreference(null);
      setGuestRoomTherapyAvailable(false);
      setAssignedPackage(null);
      setPackageLocked(false);
      setAssignedAdvisor('');
      setShowPackageDropdown(false);
      setShowAdvisorDropdown(false);
      setSelectedPackage(null);
      setNightsNumber(7);
      setSelectedNights('');
      setAssignedDoctor('');
      setStartDate('');
      setSchedule(null);
      scheduleLoadGuestRef.current = '';
      setGeneralInstruction('');
      setTreatmentInstructions({});
    }
  };

  const handleGuestRoomChange = (e) => {
    const newRoomNumber = e.target.value;
    setGuestRoom(newRoomNumber);
    
    // Check for in-room therapy when room number is manually entered
    checkInRoomTherapy(newRoomNumber);
  };

  // Notify AIIMS when package is changed
  const notifyAIMSPackageChange = async (packageName, guestIdValue, bookingIdValue) => {
    console.log('🔍 notifyAIMSPackageChange called with:', {
      packageName,
      guestId: guestIdValue,
      bookingId: bookingIdValue
    });

    if (!guestIdValue || !packageName) {
      console.warn('⚠️ Skipping AIIMS package notification - missing required data');
      return;
    }

    try {
      console.log(`📤 Notifying AIIMS of package change: ${packageName} for guest ${guestIdValue}`);

      // Call the package change notification webhook
      const response = await apiPost('/api/admin/package-notification', {
        packageName: packageName,
        guestId: guestIdValue,
        bookingId: bookingIdValue || ''
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ AIIMS package notification sent:', result);
      } else {
        const errorData = await response.json();
        console.warn('⚠️ AIIMS package notification failed:', errorData.detail || response.statusText);
      }
    } catch (error) {
      console.error('❌ Error notifying AIIMS of package change:', error);
      // Don't throw - this is a non-critical background operation
    }
  };

  // Save guest package change to our database
  const saveGuestPackageChange = async (packageName, guestIdValue, recordId, nightsValue, options = {}) => {
    const { preserveNights = false } = options;
    console.log('💾 saveGuestPackageChange called with:', {
      packageName,
      guestId: guestIdValue,
      recordId,
      nights: nightsValue,
      preserveNights,
    });

    if (!guestIdValue || !packageName) {
      console.warn('⚠️ Skipping guest package save - missing required data');
      return;
    }

    try {
      console.log(`💾 Saving guest package change to database: ${packageName} for guest ${guestIdValue}`);

      // Call the new endpoint to update just the package info
      const response = await apiPost(`/api/package-schedules/guest/${guestIdValue}/update-package`, {
        package_name: packageName,
        package_record_id: recordId || '',
        nights: nightsValue ? nightsValue.toString() : '0',
        preserve_nights: preserveNights,
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Guest package saved to database:', result);
      } else {
        const errorData = await response.json();
        console.warn('⚠️ Failed to save guest package:', errorData.detail || response.statusText);
      }
    } catch (error) {
      console.error('❌ Error saving guest package to database:', error);
      // Don't throw - this is a non-critical background operation
    }
  };
  const notifyAIMSDoctorAssignment = async (doctorName, guestIdValue, externalBookingIdValue) => {
    console.log('🔍 notifyAIMSDoctorAssignment called with:', {
      doctorName,
      guestIdValue,
      externalBookingIdValue
    });
    
    if (!doctorName || !guestIdValue) {
      console.log('⚠️ Skipping AIIMS notification - missing doctor or guest ID');
      return;
    }

    try {
      console.log(`📤 Notifying AIIMS of doctor assignment: ${doctorName} for guest ${guestIdValue}`);
      
      // Call the booking notification webhook
      // CRITICAL: bookingId should be the external booking ID (BKG format from resort system)
      // Backend will lookup doctor email from database if not provided
      const response = await apiPost('/api/admin/booking-notification', {
        doctorName: doctorName,
        guestId: guestIdValue,
        bookingId: externalBookingIdValue || '', // External booking ID (BKG format)
        uid: 2
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ AIIMS notified successfully:', result.message);
      } else {
        const errorData = await response.json();
        console.warn('⚠️ AIIMS notification failed:', errorData.detail || response.statusText);
      }
    } catch (error) {
      console.error('❌ Error notifying AIIMS:', error);
      // Don't throw - this is a non-critical background operation
    }
  };

  // Treatment editing handlers
  const handleTreatmentChange = (dayIndex, treatmentIndex, newTreatmentData) => {
    setSchedule(prevSchedule => {
      const newSchedule = { ...prevSchedule };
      const updatedSchedule = [...newSchedule.schedule];
      const day = { ...updatedSchedule[dayIndex] };
      const treatments = [...day.treatments];
      
      treatments[treatmentIndex] = {
        ...treatments[treatmentIndex],
        name: newTreatmentData.name,
        duration: newTreatmentData.duration,
        prepTime: newTreatmentData.prepTime || 10,
        cleanupTime: newTreatmentData.cleanupTime || 15,
        type: newTreatmentData.type || 'WELLNESS',
        tier_level: newTreatmentData.tier_level, // CRITICAL: Preserve tier_level
        isUpgraded: newTreatmentData.isUpgraded || treatments[treatmentIndex].isUpgraded || false // Preserve upgrade flag
      };
      
      day.treatments = treatments;
      updatedSchedule[dayIndex] = day;
      newSchedule.schedule = updatedSchedule;
      
      return newSchedule;
    });
  };

  // Handle standalone treatment approval
  const handleStandaloneApprove = (approvalData) => {
    if (!standaloneTreatmentData) return;
    
    const { dayIndex, treatmentIndex, treatment } = standaloneTreatmentData;
    const day = schedule.schedule[dayIndex];
    const existingTreatment = day.treatments[treatmentIndex];
    const oldInclusionId = existingTreatment.inclusionId;
    const treatmentOrder = existingTreatment.order || treatmentIndex + 1;
    const newInclusionId = generateDeterministicId(guestId, day.day, treatment.name, treatmentOrder);
    
    const newStandaloneApprovals = { ...standaloneApprovals };
    const priorApproval = newStandaloneApprovals[oldInclusionId];
    if (oldInclusionId && oldInclusionId !== newInclusionId) {
      delete newStandaloneApprovals[oldInclusionId];
    }
    newStandaloneApprovals[newInclusionId] = { ...(priorApproval || {}), ...approvalData };
    setStandaloneApprovals(newStandaloneApprovals);
    
    setSchedule(prevSchedule => {
      const newSchedule = { ...prevSchedule };
      const updatedSchedule = [...newSchedule.schedule];
      const dayCopy = { ...updatedSchedule[dayIndex] };
      const treatments = [...dayCopy.treatments];
      
      treatments[treatmentIndex] = {
        ...treatments[treatmentIndex],
        inclusionId: newInclusionId,
        name: treatment.name,
        duration: treatment.duration,
        prepTime: treatment.preparation_time || 10,
        cleanupTime: treatment.cleanup_time || 15,
        type: treatment.type,
        tier_level: treatment.tier_level,
        isManuallyAdded: true,
        isDailyInclusion: false
      };
      
      dayCopy.treatments = treatments;
      updatedSchedule[dayIndex] = dayCopy;
      newSchedule.schedule = updatedSchedule;
      return newSchedule;
    });
    
    setShowStandaloneApprovalModal(false);
    setStandaloneTreatmentData(null);
    setEditingTreatment(null);
    
    console.log('✅ [STANDALONE APPROVE] Standalone approval stored with deterministic ID:', newInclusionId);
  };

  const handleStandaloneCancel = () => {
    // Cancel adding the treatment - remove it from schedule
    if (standaloneTreatmentData) {
      const { dayIndex, treatmentIndex } = standaloneTreatmentData;
      handleDeleteTreatment(dayIndex, treatmentIndex);
    }
    setShowStandaloneApprovalModal(false);
    setStandaloneTreatmentData(null);
    setEditingTreatment(null);
  };

  const handleUpgradeApprove = (approvalData) => {
    if (!pendingUpgradeChange || !upgradePreview) return;
    
    const { dayIndex, treatmentIndex, newTreatment, originalTierValue } = pendingUpgradeChange;
    
    // Store upgrade approval data for later use when booking
    const inclusionId = schedule.schedule[dayIndex].treatments[treatmentIndex].inclusionId;
    console.log('🔼 [UPGRADE APPROVE] Storing upgrade approval for inclusionId:', inclusionId);
    console.log('🔼 [UPGRADE APPROVE] Original tier:', upgradePreview.original_tier_level);
    console.log('🔼 [UPGRADE APPROVE] New tier:', upgradePreview.new_tier_level);
    console.log('🔼 [UPGRADE APPROVE] Approval data received from modal:', approvalData);
    
    const upgradeApprovalData = {
      ...approvalData,
      original_tier_level: upgradePreview.original_tier_level,  // CRITICAL: Store original tier
      original_tier_value: upgradePreview.original_tier_value,
      upgrade_amount: upgradePreview.upgrade_amount,  // CRITICAL: Pre-calculated approved amount
      approved_at: new Date().toISOString(),
      approved_by: 'current_user' // TODO: Get actual user from auth context
    };
    
    console.log('🔼 [UPGRADE APPROVE] Full upgrade approval data to store:', upgradeApprovalData);
    
    // Create new upgrade approvals object (needed for auto-save)
    const newUpgradeApprovals = {
      ...upgradeApprovals,
      [inclusionId]: upgradeApprovalData
    };
    
    setUpgradeApprovals(newUpgradeApprovals);
    
    console.log('🔼 [UPGRADE APPROVE] New upgradeApprovals state:', newUpgradeApprovals);
    
    // Update treatment in schedule with upgrade flag
    handleTreatmentChange(dayIndex, treatmentIndex, {
      ...newTreatment,
      isUpgraded: true  // Mark as upgraded
    });
    
    // Close modal
    setShowUpgradeModal(false);
    setUpgradePreview(null);
    setPendingUpgradeChange(null);
    
    // AUTO-SAVE DISABLED: React state timing issues cause treatment name to not update before save
    // User must click the SAVE button in the edit panel to persist the upgrade
    console.log('✅ [UPGRADE APPROVE] Upgrade approval stored. Click SAVE to persist to database.');
  };

  const handleUpgradeCancel = () => {
    // Cancel the upgrade - REVERT the treatment change
    console.log('❌ [UPGRADE CANCEL] User cancelled upgrade');
    
    // Get the original treatment from pendingUpgradeChange (now includes originalTreatment)
    if (pendingUpgradeChange && pendingUpgradeChange.originalTreatment) {
      const { dayIndex, treatmentIndex, originalTreatment, originalTreatmentName } = pendingUpgradeChange;
      
      console.log(`❌ [UPGRADE CANCEL] Reverting from "${pendingUpgradeChange.newTreatment.name}" back to: "${originalTreatmentName}"`);
      
      // Revert to original treatment
      handleTreatmentChange(dayIndex, treatmentIndex, {
        name: originalTreatment.name,
        duration: originalTreatment.duration,
        prepTime: originalTreatment.preparation_time || 10,
        cleanupTime: originalTreatment.cleanup_time || 15,
        type: originalTreatment.type,
        tier_level: originalTreatment.tier_level,
        isUpgraded: false  // Clear upgrade flag
      });
    }
    
    setShowUpgradeModal(false);
    setUpgradePreview(null);
    setPendingUpgradeChange(null);
  };

  const handleDurationChange = (dayIndex, treatmentIndex, newDuration) => {
    setSchedule(prevSchedule => {
      const newSchedule = { ...prevSchedule };
      const updatedSchedule = [...newSchedule.schedule];
      const day = { ...updatedSchedule[dayIndex] };
      const treatments = [...day.treatments];
      
      treatments[treatmentIndex] = {
        ...treatments[treatmentIndex],
        duration: parseInt(newDuration) || 50
      };
      
      day.treatments = treatments;
      updatedSchedule[dayIndex] = day;
      newSchedule.schedule = updatedSchedule;
      
      return newSchedule;
    });
  };

  const remapInclusionMetadata = (oldId, newId) => {
    if (!oldId || !newId || oldId === newId) return;

    setStandaloneApprovals((prev) => {
      if (!prev[oldId]) return prev;
      const next = { ...prev };
      next[newId] = next[oldId];
      delete next[oldId];
      return next;
    });

    setUpgradeApprovals((prev) => {
      if (!prev[oldId]) return prev;
      const next = { ...prev };
      next[newId] = next[oldId];
      delete next[oldId];
      return next;
    });

    setTreatmentInstructions((prev) => {
      if (!prev[oldId]) return prev;
      const next = { ...prev };
      next[newId] = next[oldId];
      delete next[oldId];
      return next;
    });
  };

  const getConsultationDateBounds = () => {
    const checkInISO = guestArrivalDate || startDate || schedule?.schedule?.[0]?.date || '';
    const minDate = checkInISO;
    const checkoutISO = guestDepartureDate
      ? getCheckOutISO({
          checkOut: guestDepartureDate,
          checkout: guestDepartureDate,
          departureDate: guestDepartureDate,
        })
      : null;
    const lastScheduleDate = schedule?.schedule?.[schedule.schedule.length - 1]?.date;
    const maxDate =
      checkoutISO ||
      (lastScheduleDate ? addDaysISO(lastScheduleDate, 1) : minDate);
    return { minDate, maxDate };
  };

  /** Package start can be any day from check-in through checkout (same window as per-treatment moves). */
  const getPackageStartDateBounds = () => getConsultationDateBounds();

  const handleShiftPackageStartDate = async (newStartISO) => {
    if (!schedule?.schedule?.length || !startDate || !newStartISO || newStartISO === startDate) {
      return;
    }

    const deltaDays = dayNumberFromDate(startDate, newStartISO) - 1;
    if (deltaDays === 0) return;

    const { minDate, maxDate: checkoutISO } = getPackageStartDateBounds();
    if (minDate && newStartISO < minDate) {
      alert(`Package cannot start before guest check-in (${minDate}).`);
      return;
    }
    if (checkoutISO && newStartISO > checkoutISO) {
      alert(`Package cannot start after guest checkout (${checkoutISO}).`);
      return;
    }

    const shiftDate = (iso) => (iso ? addDaysISO(iso, deltaDays) : iso);

    const updatedSchedule = schedule.schedule.map((day) => {
      const newDate = shiftDate(day.date);
      return {
        ...day,
        date: newDate,
        dayOfWeek: new Date(`${newDate}T12:00:00`).toLocaleDateString('en-US', {
          weekday: 'long',
        }),
      };
    });

    const lastShiftedDate = updatedSchedule[updatedSchedule.length - 1]?.date;
    if (checkoutISO && lastShiftedDate && lastShiftedDate > checkoutISO) {
      const proceed = window.confirm(
        `Shifting to ${newStartISO} moves the last treatment to ${lastShiftedDate}, after checkout (${checkoutISO}).\n\n` +
          'Continue anyway? You may need to move or remove treatments, or extend the stay in CRM before booking.',
      );
      if (!proceed) return;
    }

    setStartDate(newStartISO);
    setSchedule((prev) => ({
      ...prev,
      schedule: updatedSchedule,
      startDate: newStartISO,
    }));

    setBookingResults((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        bookings: (prev.bookings || []).map((b) => ({ ...b, date: shiftDate(b.date) })),
        failures: (prev.failures || []).map((f) => ({ ...f, date: shiftDate(f.date) })),
      };
    });

    try {
      const packageToUse = selectedPackage || assignedPackage;
      const recordId =
        packageToUse?.recordId || packageToUse?.programId || packageToUse?.programRecordId || packageToUse?.id;
      if (packageToUse && recordId) {
        await saveScheduleToDatabase(
          { ...schedule, schedule: updatedSchedule },
          packageToUse,
          recordId,
          null,
          null,
          newStartISO,
        );
      }
    } catch (err) {
      console.warn('Could not persist shifted start date:', err);
    }
  };

  const handleConsultationDateChange = (sourceDayIndex, treatmentIndex, newDateISO) => {
    if (!schedule?.schedule || !startDate || !newDateISO || !guestId) return;

    const sourceDay = schedule.schedule[sourceDayIndex];
    const treatment = sourceDay?.treatments?.[treatmentIndex];
    if (!sourceDay || !treatment) return;
    if (newDateISO === sourceDay.date) return;

    const { minDate, maxDate } = getConsultationDateBounds();
    if (minDate && newDateISO < minDate) {
      alert(`Treatment cannot be scheduled before check-in (${minDate}).`);
      return;
    }
    if (maxDate && newDateISO > maxDate) {
      alert(`Treatment cannot be scheduled after checkout (${maxDate}).`);
      return;
    }

    const oldInclusionId = treatment.inclusionId;
    const moveResult = moveConsultationInSchedule(
      schedule.schedule,
      sourceDayIndex,
      treatmentIndex,
      newDateISO,
      startDate,
      guestId
    );
    if (!moveResult) return;

    setSchedule((prevSchedule) => ({
      ...prevSchedule,
      schedule: moveResult.schedule,
    }));

    if (oldInclusionId !== moveResult.newInclusionId) {
      remapInclusionMetadata(oldInclusionId, moveResult.newInclusionId);
    }

    setBookingResults((prev) => {
      if (!prev?.failures?.length) return prev;
      const failures = prev.failures.filter((failure) => {
        if (failure.inclusionId && failure.inclusionId === oldInclusionId) return false;
        return !(
          failure.treatmentName === treatment.name &&
          failure.date === sourceDay.date
        );
      });
      if (failures.length === prev.failures.length) return prev;
      return {
        ...prev,
        failures,
        failed_count: failures.length,
        summary: prev.summary
          ? { ...prev.summary, failed: failures.length }
          : prev.summary,
      };
    });

    setEditingTreatment({
      dayIndex: moveResult.newDayIndex,
      treatmentIndex: moveResult.newTreatmentIndex,
    });
  };

  const startEditingTreatment = (dayIndex, treatmentIndex) => {
    setEditingTreatment({ dayIndex, treatmentIndex });
  };

  const cancelTreatmentEdit = () => {
    setEditingTreatment(null);
  };

  const saveTreatmentEdit = async (dayIndex, treatmentIndex) => {
    // DON'T close modal yet - save first!
    console.log('💾 [SAVE EDIT] Starting save...');
    console.log('💾 [SAVE EDIT] Schedule state:', schedule);
    console.log('💾 [SAVE EDIT] Editing treatment at day', dayIndex, 'index', treatmentIndex);
    
    try {
      const packageToUse = selectedPackage || assignedPackage;
      const recordId = packageToUse?.recordId || packageToUse?.programId || packageToUse?.programRecordId || packageToUse?.id;
      
      console.log('💾 [SAVE EDIT] Package:', packageToUse);
      console.log('💾 [SAVE EDIT] Record ID:', recordId);
      console.log('💾 [SAVE EDIT] Has schedule:', !!schedule);
      
      if (schedule && packageToUse && recordId) {
        console.log('💾 [SAVE EDIT] Calling saveScheduleToDatabase NOW...');
        await saveScheduleToDatabase(schedule, packageToUse, recordId);
        console.log('💾 [SAVE EDIT] ✅ Save completed successfully!');
        alert('✅ Changes saved successfully!');
      } else {
        const missing = [];
        if (!schedule) missing.push('schedule');
        if (!packageToUse) missing.push('package');
        if (!recordId) missing.push('recordId');
        console.error('💾 [SAVE EDIT] ❌ Missing:', missing.join(', '));
        alert(`Cannot save: Missing ${missing.join(', ')}`);
      }
    } catch (error) {
      console.error('💾 [SAVE EDIT] ❌ Save failed:', error);
      alert(`Failed to save: ${error.message}`);
    } finally {
      // Close modal after save attempt
      setEditingTreatment(null);
    }
  };

  const handleAddTreatment = (dayIndex) => {
    setSchedule(prevSchedule => {
      const newSchedule = { ...prevSchedule };
      const updatedSchedule = [...newSchedule.schedule];
      const day = { ...updatedSchedule[dayIndex] };
      const treatments = [...day.treatments];
      
      // Generate a temporary ID for the new treatment (will be replaced with deterministic ID when name is selected)
      const tempOrder = treatments.length + 1;
      const newTreatment = {
        inclusionId: `manual_temp_${Date.now()}`, // Temporary - will be replaced when treatment is selected
        name: '',
        duration: 50,
        prepTime: 10,
        cleanupTime: 15,
        type: 'WELLNESS',
        isDailyInclusion: false,
        order: tempOrder,
        isManuallyAdded: true // Flag to identify manually added treatments
      };
      
      treatments.push(newTreatment);
      day.treatments = treatments;
      updatedSchedule[dayIndex] = day;
      newSchedule.schedule = updatedSchedule;
      
      // Auto-enter edit mode for the new treatment
      setEditingTreatment({ 
        dayIndex: dayIndex, 
        treatmentIndex: treatments.length - 1 
      });
      
      return newSchedule;
    });
  };

  const handleDeleteTreatment = (dayIndex, treatmentIndex) => {
    if (window.confirm('Are you sure you want to remove this treatment?')) {
      setSchedule(prevSchedule => {
        const newSchedule = { ...prevSchedule };
        const updatedSchedule = [...newSchedule.schedule];
        const day = { ...updatedSchedule[dayIndex] };
        const treatments = [...day.treatments];
        
        // Remove the treatment
        treatments.splice(treatmentIndex, 1);
        
        day.treatments = treatments;
        updatedSchedule[dayIndex] = day;
        newSchedule.schedule = updatedSchedule;
        
        // Update total treatments count
        newSchedule.totalTreatments = updatedSchedule.reduce(
          (sum, d) => sum + d.treatments.length, 
          0
        );
        
        // Close edit mode
        setEditingTreatment(null);
        
        return newSchedule;
      });
    }
  };

  // Add/Remove entire days from schedule
  const handleAddDay = () => {
    // First, validate that we can add another day
    if (!schedule || !schedule.schedule || schedule.schedule.length === 0) {
      alert('No schedule to add a day to');
      return;
    }
    
    const lastDay = schedule.schedule[schedule.schedule.length - 1];
    const lastDate = new Date(lastDay.date);
    const newDate = new Date(lastDate);
    newDate.setDate(newDate.getDate() + 1);
    const newDateStr = newDate.toISOString().split('T')[0];
    
    // Validate: Check if new date is within guest's stay period
    if (guestDepartureDate) {
      // Normalize departure date to ISO format for comparison
      const departureDateISO = getCheckOutISO({ checkOut: guestDepartureDate, checkout: guestDepartureDate, departureDate: guestDepartureDate });
      
      if (departureDateISO && newDateStr > departureDateISO) {
        alert(`Cannot add Day ${lastDay.day + 1} (${newDateStr}).\n\nThe guest's checkout date is ${new Date(departureDateISO).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}.\n\nPlease extend the guest's stay in the CRM system first before adding more days to the schedule.`);
        return;
      }
    }
    
    setSchedule(prevSchedule => {
      if (!prevSchedule || !prevSchedule.schedule) return prevSchedule;
      
      const newSchedule = { ...prevSchedule };
      const updatedSchedule = [...newSchedule.schedule];
      const lastDayInner = updatedSchedule[updatedSchedule.length - 1];
      
      // Calculate new day number and date
      const newDayNumber = lastDayInner.day + 1;
      
      // Determine departure time category from guest's ETD
      let departureCategory = 'unknown'; // unknown, morning, afternoon, evening
      let treatments = [];
      
      if (guestCheckoutETD) {
        try {
          // Parse ETD (format: "HH:MM" or "HH:MM AM/PM")
          const etdLower = guestCheckoutETD.toLowerCase().trim();
          let hours = 0;
          
          if (etdLower.includes('am') || etdLower.includes('pm')) {
            // 12-hour format
            const match = etdLower.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)/);
            if (match) {
              hours = parseInt(match[1]);
              const isPM = match[3] === 'pm';
              if (isPM && hours !== 12) hours += 12;
              if (!isPM && hours === 12) hours = 0;
            }
          } else {
            // 24-hour format
            const match = etdLower.match(/(\d{1,2}):?(\d{2})?/);
            if (match) {
              hours = parseInt(match[1]);
            }
          }
          
          // Categorize departure time
          if (hours > 0) {
            if (hours < 12) {
              departureCategory = 'morning';
            } else if (hours < 15) {
              departureCategory = 'afternoon';
            } else {
              departureCategory = 'evening';
            }
          }
        } catch (e) {
          console.warn('Could not parse ETD:', guestCheckoutETD, e);
        }
      }
      
      // Add treatments based on departure time
      if (departureCategory === 'morning') {
        // Morning departure: No treatments (no time)
        treatments = [];
        console.log(`➕ Morning departure (${guestCheckoutETD}) - No treatments added`);
      } else if (departureCategory === 'afternoon' || departureCategory === 'evening') {
        // Afternoon/Evening: Add 1 Daily Inclusion
        treatments = [{
          inclusionId: `manual_departure_${guestId}_day${newDayNumber}_di_1`,
          name: 'Daily Inclusion',
          duration: 50,
          prepTime: 10,
          cleanupTime: 15,
          type: 'WELLNESS',
          isDailyInclusion: true,
          order: 1,
          isManuallyAdded: true
        }];
        console.log(`➕ ${departureCategory.charAt(0).toUpperCase() + departureCategory.slice(1)} departure (${guestCheckoutETD}) - Added 1 Daily Inclusion`);
      } else {
        // Unknown departure time: Safe default - add 1 Daily Inclusion
        treatments = [{
          inclusionId: `manual_departure_${guestId}_day${newDayNumber}_di_1`,
          name: 'Daily Inclusion',
          duration: 50,
          prepTime: 10,
          cleanupTime: 15,
          type: 'WELLNESS',
          isDailyInclusion: true,
          order: 1,
          isManuallyAdded: true
        }];
        console.log(`➕ Unknown departure time - Added 1 Daily Inclusion (safe default)`);
      }
      
      const newDay = {
        day: newDayNumber,
        date: newDateStr,
        dayLabel: `Day ${newDayNumber}`,
        treatments: treatments
      };
      
      updatedSchedule.push(newDay);
      newSchedule.schedule = updatedSchedule;
      newSchedule.totalTreatments = updatedSchedule.reduce((sum, d) => sum + d.treatments.length, 0);
      
      console.log(`➕ Added Day ${newDayNumber} (${newDateStr})`);
      return newSchedule;
    });
  };

  const handleRemoveLastDay = () => {
    if (window.confirm('Remove the last day from the schedule?')) {
      setSchedule(prevSchedule => {
        if (!prevSchedule || !prevSchedule.schedule || prevSchedule.schedule.length <= 1) {
          alert('Cannot remove the only day in the schedule');
          return prevSchedule;
        }
        
        const newSchedule = { ...prevSchedule };
        const updatedSchedule = [...newSchedule.schedule];
        const removedDay = updatedSchedule.pop(); // Remove last day
        
        newSchedule.schedule = updatedSchedule;
        newSchedule.totalTreatments = updatedSchedule.reduce((sum, d) => sum + d.treatments.length, 0);
        
        console.log(`➖ Removed Day ${removedDay.day} (${removedDay.date})`);
        return newSchedule;
      });
    }
  };

  const handleDeleteItinerary = async () => {
    if (!guestId || !schedule) return;
    const confirmed = window.confirm(
      'Delete this guest’s full itinerary?\n\nBooked treatments will be removed and those times freed. Use this when the guest is a no-show or the stay is cancelled.'
    );
    if (!confirmed) return;

    try {
      const response = await apiDelete(
        `/api/package-schedules/guest/${encodeURIComponent(guestId)}/itinerary`
      );
      if (!response.ok && response.status !== 404) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete itinerary');
      }
      setSchedule(null);
      setBookingResults(null);
      setExistingBookings([]);
      setHasAttemptedBooking(false);
      setTreatmentInstructions({});
      setGeneralInstruction('');
      alert('Itinerary deleted.');
    } catch (error) {
      console.error('Failed to delete itinerary:', error);
      alert(error.message || 'Failed to delete itinerary');
    }
  };

  // Handle retry booking for failed treatments
  const handleRetryBooking = async (dayIndex, treatmentIndex, treatment, date) => {
    console.log('🔄 Retry booking for:', treatment.name, 'on', date);
    
    setRetryTreatmentData({
      dayIndex,
      treatmentIndex,
      treatment,
      date
    });
    
    setShowRetryModal(true);
    setRetryLoading(true);
    setAvailableSlots([]);
    setRetryFailureHints([]);
    
    try {
      // Mirror package booking: only consultations use assigned doctor as preferred therapist
      const metaType = (
        treatment.type ||
        availableTreatments.find((t) => t.name === treatment.name)?.type ||
        ''
      ).toUpperCase();
      const isConsultationType = metaType.includes('CONSULTATION');

      // Use avoidTimes so each preview returns a different slot (not the same earliest time).
      const relaxedStrategies = [
        { name: 'Flexible time', timeOfDay: null, anyRoom: false, anyTherapist: false },
        { name: 'Any room', timeOfDay: null, anyRoom: true, anyTherapist: false },
        { name: 'Any therapist', timeOfDay: null, anyRoom: false, anyTherapist: true },
        { name: 'Fully relaxed', timeOfDay: null, anyRoom: true, anyTherapist: true },
      ];

      const MAX_SLOTS_PER_PERIOD = 10;
      const MAX_SLOTS_TOTAL = 20;
      const foundSlots = [];
      const seenSlotKeys = new Set();
      const avoidTimes = [];
      const failureHints = [];

      const recordAvoidTime = (slot) => {
        const startMin = parseTimeToMinutes(slot.startTime);
        if (startMin != null && !avoidTimes.includes(startMin)) {
          avoidTimes.push(startMin);
        }
      };

      const tryPreview = async (strategy) => {
        const preferDoctor =
          isConsultationType && !strategy.anyTherapist ? assignedDoctor || null : null;

        const requestData = {
          treatment: treatment.name,
          date,
          guestName,
          guestId,
          guestGender,
          guestRoom: guestRoom != null && guestRoom !== '' ? String(guestRoom) : '',
          preferredTimeOfDay: strategy.timeOfDay,
          therapistPreference: preferDoctor,
          roomPreference: strategy.anyRoom ? null : (roomPreference?.name || null),
          genderPreference: therapistGenderPreference || null,
          avoidTimes: [...avoidTimes],
        };

        console.log(`  Trying strategy: ${strategy.name}`, requestData);

        const response = await apiPost('/api/schedule/preview', requestData);

        if (!response.ok) {
          let detail = `${response.status} ${response.statusText}`;
          try {
            const errBody = await response.json();
            if (errBody?.detail) {
              detail = typeof errBody.detail === 'string' ? errBody.detail : JSON.stringify(errBody.detail);
            }
          } catch {
            /* ignore parse errors */
          }
          failureHints.push(`${strategy.name}: ${detail}`);
          console.warn(`  Preview [${strategy.name}] HTTP error:`, detail);
          return null;
        }

        const slot = await response.json();

        if (slot?.error) {
          const msg = typeof slot.error === 'string' ? slot.error : JSON.stringify(slot.error);
          const typeSuffix = slot.error_type ? ` [${slot.error_type}]` : '';
          failureHints.push(`${strategy.name}:${typeSuffix} ${msg}`);
          console.warn(`  Preview [${strategy.name}] scheduler message:`, slot.error, slot.error_type || '');
          return null;
        }

        if (!slot || slot.error) return null;

        const roomData = allRoomsData[slot.room];
        const isInRoomTherapy = roomData?.location?.startsWith('Room No:');
        const guestRoomLocation = guestRoom ? `Room No:${guestRoom}` : null;

        if (isInRoomTherapy && roomData.location !== guestRoomLocation) {
          console.log(`  ⏭️ Skipping in-room therapy: ${slot.room} (location: ${roomData.location}) - not guest's room`);
          failureHints.push(`${strategy.name}: Skipped (in-room therapy room ${slot.room} — not guest room)`);
          recordAvoidTime(slot);
          return null;
        }

        return slot;
      };

      const addSlot = (slot, strategyName) => {
        const key = slotDedupeKey(slot);
        recordAvoidTime(slot);
        if (seenSlotKeys.has(key)) return false;
        seenSlotKeys.add(key);
        const startMinutes = parseTimeToMinutes(slot.startTime) ?? 0;
        foundSlots.push({
          ...slot,
          strategy: strategyName,
          period: classifySlotPeriod(slot.startTime),
          startMinutes,
          timeOfDay: slot.preferredTimeOfDay || strategyName,
          startTimeFormatted: formatTime(slot.startTime),
          endTimeFormatted: formatTime(slot.endTime),
        });
        return true;
      };

      const exhaustStrategy = async (strategy, maxForStrategy) => {
        let stagnantAttempts = 0;
        let addedForStrategy = 0;
        while (
          addedForStrategy < maxForStrategy &&
          foundSlots.length < MAX_SLOTS_TOTAL
        ) {
          const slot = await tryPreview(strategy);
          if (!slot) break;

          const added = addSlot(slot, strategy.name);
          if (!added) {
            stagnantAttempts += 1;
            if (stagnantAttempts >= 3) break;
          } else {
            stagnantAttempts = 0;
            addedForStrategy += 1;
          }
        }
      };

      // Fill morning and afternoon columns separately
      await exhaustStrategy(
        { name: 'Morning', timeOfDay: 'morning', anyRoom: false, anyTherapist: false },
        MAX_SLOTS_PER_PERIOD,
      );
      await exhaustStrategy(
        { name: 'Afternoon', timeOfDay: 'afternoon', anyRoom: false, anyTherapist: false },
        MAX_SLOTS_PER_PERIOD,
      );
      if (isConsultationType) {
        await exhaustStrategy(
          { name: 'Afternoon (any doctor)', timeOfDay: 'afternoon', anyRoom: false, anyTherapist: true },
          MAX_SLOTS_PER_PERIOD,
        );
      }

      for (const strategy of relaxedStrategies) {
        if (foundSlots.length >= MAX_SLOTS_TOTAL) break;
        await exhaustStrategy(strategy, 4);
      }
      
      setAvailableSlots(foundSlots);
      const uniqueHints = [...new Set(failureHints)].slice(0, 8);
      setRetryFailureHints(uniqueHints);
      
    } catch (error) {
      console.error('Error finding alternative slots:', error);
      alert(`Error finding slots: ${error.message}`);
    } finally {
      setRetryLoading(false);
    }
  };
  
  // Handle selecting a slot from retry modal
  const handleSelectRetrySlot = async (slot) => {
    if (!retryTreatmentData) return;
    
    const { dayIndex, treatmentIndex, treatment, date } = retryTreatmentData;
    
    try {
      // Book the selected slot
      const bookingId = `BKG-${Date.now()}`;
      const isStandaloneLineItem = Boolean(treatment.isManuallyAdded);
      const pkg = selectedPackage || assignedPackage;
      const pkgId = pkg?.id ?? pkg?.recordId ?? pkg?.programId ?? pkg?.programRecordId;
      const pkgName = pkg?.name || pkg?.packageName || null;

      const bookingData = {
        bookingId: bookingId,
        treatment: treatment.name,
        date: date,
        guestName: guestName,
        guestId: guestId,
        startTime: slot.startTime,
        endTime: slot.endTime,
        therapist: slot.therapist,
        therapists: slot.therapists || [{ name: slot.therapist, is_primary: true }],
        therapistsCount: slot.therapistsCount || 1,
        room: slot.room,
        duration: treatment.duration,
        generalInstruction: generalInstruction,
        treatmentInstruction: treatmentInstructions[treatment.name] || '',
        // Billing: package inclusions must hit saveBooking with is_package_service true (repository default is false)
        is_package_service: !isStandaloneLineItem,
        ...(pkgId && !isStandaloneLineItem ? { package_id: String(pkgId) } : {}),
        ...(pkgName && !isStandaloneLineItem ? { package_name: pkgName } : {}),
        ...(isStandaloneLineItem && standaloneApprovals[treatment.inclusionId]
          ? {
              standalone_billing_status: standaloneApprovals[treatment.inclusionId].billing_status || 'payable',
              standalone_discount_amount: standaloneApprovals[treatment.inclusionId].discount_amount || 0,
              standalone_reason: standaloneApprovals[treatment.inclusionId].reason || '',
              standalone_approved_by: standaloneApprovals[treatment.inclusionId].approved_by || 'system'
            }
          : {})
      };

      const response = await apiPost('/api/schedule/confirm', bookingData);
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Booking successful:', result);
        
        // Refresh bookings
        if (guestId && startDate && nightsNumber > 0) {
          const endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + nightsNumber);
          const endDateStr = endDate.toISOString().split('T')[0];
          
          const updatedBookings = await fetchExistingBookings(guestId, startDate, endDateStr);
          setExistingBookings(updatedBookings);
          
          // Update booking results
          if (bookingResults && schedule) {
            const failures = [];
            const bookedTreatments = new Set(updatedBookings.map(b => `${b.treatment}_${b.date}`));
            
            schedule.schedule.forEach(day => {
              day.treatments.forEach(treatment => {
                const key = `${treatment.name}_${day.date}`;
                if (!bookedTreatments.has(key)) {
                  failures.push({
                    treatmentName: treatment.name,
                    date: day.date,
                    reason: 'Not yet booked',
                    type: treatment.type || 'unknown'
                  });
                }
              });
            });
            
            setBookingResults({
              success: updatedBookings.length > 0 && failures.length === 0,
              bookings: updatedBookings.map(b => ({
                treatmentName: b.treatment,
                date: b.date,
                startTime: b.startTime || b.start_time,
                endTime: b.endTime || b.end_time,
                therapist: b.therapist,
                room: b.room,
                bookingId: b.bookingId,
                duration: b.duration,
                custom_duration: b.custom_duration,
                isExisting: true
              })),
              booked_count: updatedBookings.length,
              failures: failures,
              failed_count: failures.length,
              summary: {
                total: schedule.schedule.reduce((sum, day) => sum + day.treatments.length, 0),
                booked: updatedBookings.length,
                failed: failures.length
              }
            });
          }
        }
        
        alert(`✅ Successfully booked ${treatment.name}!\n\nTime: ${slot.startTimeFormatted} - ${slot.endTimeFormatted}\nTherapist: ${slot.therapist}\nRoom: ${slot.room}`);
        
        // Close modals and exit edit mode
        setShowRetryModal(false);
        setRetryTreatmentData(null);
        setEditingTreatment(null);
        
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to book appointment');
      }
      
    } catch (error) {
      console.error('Error booking slot:', error);
      alert(`Error booking appointment: ${error.message}`);
    }
  };

  // Handle EditBookingModal completion (when a booked treatment is edited)
  const handleEditBookingComplete = async (result) => {
    console.log('EditBookingModal completed:', result);
    
    if (result?.success) {
      // Refresh existing bookings for this date range
      try {
        if (guestId && startDate && nightsNumber > 0) {
          const endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + nightsNumber);
          const endDateStr = endDate.toISOString().split('T')[0];
          
          console.log('Refreshing bookings after edit...');
          
          // FIRST REFRESH: Immediate
          const updatedBookings = await fetchExistingBookings(guestId, startDate, endDateStr);
          setExistingBookings(updatedBookings);
          
          // Update booking results to reflect changes
          if (bookingResults && schedule) {
            const failures = [];
            const bookedTreatments = new Set(updatedBookings.map(b => `${b.treatment}_${b.date}`));
            
            schedule.schedule.forEach(day => {
              day.treatments.forEach(treatment => {
                const key = `${treatment.name}_${day.date}`;
                if (!bookedTreatments.has(key)) {
                  failures.push({
                    treatmentName: treatment.name,
                    date: day.date,
                    reason: 'Not yet booked',
                    type: treatment.type || 'unknown'
                  });
                }
              });
            });
            
            setBookingResults({
              success: updatedBookings.length > 0 && failures.length === 0,
              bookings: updatedBookings.map(b => ({
                treatmentName: b.treatment,
                date: b.date,
                startTime: b.startTime || b.start_time,
                endTime: b.endTime || b.end_time,
                therapist: b.therapist,
                room: b.room,
                bookingId: b.bookingId,
                duration: b.duration,  // Custom duration
                custom_duration: b.custom_duration,  // Custom duration flag
                isExisting: true
              })),
              booked_count: updatedBookings.length,
              failures: failures,
              failed_count: failures.length,
              summary: {
                total: schedule.schedule.reduce((sum, day) => sum + day.treatments.length, 0),
                booked: updatedBookings.length,
                failed: failures.length
              }
            });
          }
          
          console.log('✅ Initial refresh complete, booking count:', updatedBookings.length);
          
          // SECOND REFRESH: After 500ms delay to ensure database is fully synced
          // This prevents race conditions where the edit transaction hasn't fully committed
          setTimeout(async () => {
            try {
              console.log('🔄 Double-checking bookings after delay...');
              const doubleCheckBookings = await fetchExistingBookings(guestId, startDate, endDateStr);
              
              // Only update if the count changed (indicates database was still syncing)
              if (doubleCheckBookings.length !== updatedBookings.length) {
                console.log(`⚠️ Booking count changed: ${updatedBookings.length} → ${doubleCheckBookings.length}`);
                setExistingBookings(doubleCheckBookings);
                
                // Update booking results again
                if (bookingResults && schedule) {
                  const failures = [];
                  const bookedTreatments = new Set(doubleCheckBookings.map(b => `${b.treatment}_${b.date}`));
                  
                  schedule.schedule.forEach(day => {
                    day.treatments.forEach(treatment => {
                      const key = `${treatment.name}_${day.date}`;
                      if (!bookedTreatments.has(key)) {
                        failures.push({
                          treatmentName: treatment.name,
                          date: day.date,
                          reason: 'Not yet booked',
                          type: treatment.type || 'unknown'
                        });
                      }
                    });
                  });
                  
                  setBookingResults({
                    success: doubleCheckBookings.length > 0 && failures.length === 0,
                    bookings: doubleCheckBookings.map(b => ({
                      treatmentName: b.treatment,
                      date: b.date,
                      startTime: b.startTime || b.start_time,
                      endTime: b.endTime || b.end_time,
                      therapist: b.therapist,
                      room: b.room,
                      bookingId: b.bookingId,
                      duration: b.duration,
                      custom_duration: b.custom_duration,
                      isExisting: true
                    })),
                    booked_count: doubleCheckBookings.length,
                    failures: failures,
                    failed_count: failures.length,
                    summary: {
                      total: schedule.schedule.reduce((sum, day) => sum + day.treatments.length, 0),
                      booked: doubleCheckBookings.length,
                      failed: failures.length
                    }
                  });
                }
                
                console.log('✅ Double-check refresh complete');
              } else {
                console.log('✅ Double-check confirmed - no changes detected');
              }
            } catch (error) {
              console.error('Failed to double-check bookings:', error);
            }
          }, 500);
        }
      } catch (error) {
        console.error('Failed to refresh bookings after edit:', error);
      }
    }
    
    // Close modal
    setShowEditBookingModal(false);
    setEditingBooking(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </div>
    );
  }

  const activePackage = assignedPackage || selectedPackage;
  const stayDatesLocked = packageLocked || Boolean(guestArrivalDate);
  const showAssignedPackageReadonly = activePackage && stayDatesLocked && !showPackageDropdown;
  const showPackageChangeOnlyDropdown = activePackage && stayDatesLocked && showPackageDropdown;

  const retrySlotsByPeriod = (() => {
    const morning = [];
    const afternoon = [];
    availableSlots.forEach((slot) => {
      if (slot.period === 'afternoon') afternoon.push(slot);
      else morning.push(slot);
    });
    const byTime = (a, b) => a.startMinutes - b.startMinutes;
    return {
      morning: morning.sort(byTime),
      afternoon: afternoon.sort(byTime),
    };
  })();

  const renderRetrySlotCard = (slot, index) => (
    <Box
      key={`${slot.period}-${slot.startMinutes}-${slot.therapist}-${index}`}
      onClick={() => handleSelectRetrySlot(slot)}
      sx={{
        p: 1.5,
        border: '1px solid #e5e7eb',
        borderRadius: 1,
        cursor: 'pointer',
        transition: 'all 0.15s',
        bgcolor: '#fff',
        '&:hover': {
          backgroundColor: 'rgba(228, 201, 132, 0.15)',
          borderColor: '#E4C984',
          boxShadow: 1,
        },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700, color: '#171717' }}>
        {slot.startTimeFormatted} – {slot.endTimeFormatted}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
        {slot.therapist}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {slot.room}
      </Typography>
    </Box>
  );

  return (
    <>
    {/* Nights Warning Dialog */}
    <Dialog open={showNightsWarning} onClose={() => {
      setShowNightsWarning(false);
      setPendingNightsValue(null);
    }}>
      <DialogTitle sx={{ backgroundColor: '#E3F2FD', borderBottom: '2px solid #2196F3' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoOutlinedIcon sx={{ color: '#2196F3' }} />
          <Typography variant="h6" component="span">
            Custom Package Duration
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          You entered <strong>{pendingNightsValue}N</strong>, which is a custom duration.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Standard retreat package durations are:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {availableNights.map(nights => (
            <Chip 
              key={nights} 
              label={`${nights}N`} 
              size="small"
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Custom durations are allowed</strong>, but please note:
          </Typography>
          <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
            <li><Typography variant="caption">System will try to match closest package (e.g., 7N for 8N)</Typography></li>
            <li><Typography variant="caption">Additional days will use "Daily Inclusions"</Typography></li>
            <li><Typography variant="caption">Ensure guest package in Zynapse matches this duration</Typography></li>
          </ul>
        </Alert>
        <Typography variant="body2" sx={{ mt: 2, fontWeight: 600 }}>
          Would you like to use <strong>{pendingNightsValue}N</strong>?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={() => {
            setShowNightsWarning(false);
            setPendingNightsValue(null);
          }}
          color="inherit"
          variant="outlined"
        >
          Go Back
        </Button>
        <Button 
          onClick={() => {
            // User confirmed - proceed with custom nights value
            setNightsNumber(pendingNightsValue);
            setSelectedNights(`${pendingNightsValue}N`);
            setSchedule(null);
            setShowNightsWarning(false);
            console.log('✅ User confirmed custom nights value:', pendingNightsValue);
            setPendingNightsValue(null);
          }}
          color="primary"
          variant="contained"
        >
          Yes, Use {pendingNightsValue}N
        </Button>
      </DialogActions>
    </Dialog>

    <div className="flex h-full w-full flex-1 overflow-hidden bg-gray-100">
      {/* Left Panel - Package Form */}
      <div className="w-[30%] min-w-[350px] max-w-[450px] flex-shrink-0 bg-white shadow-lg m-4 rounded-lg overflow-auto">
        <div className="p-4">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <CalendarMonthOutlinedIcon className="text-[#E4C984]" />
            Package Scheduling
          </h2>

          {/* STEP 1: Guest Selection - Now First! */}
          {/* Guest Filter Toggle */}
          <div className="mb-3">
            <ButtonGroup 
              fullWidth 
              variant="outlined" 
              sx={{
                '& .MuiButton-root': {
                  borderColor: '#E4C984',
                  color: '#666',
                  '&:hover': {
                    borderColor: '#E4C984',
                    backgroundColor: 'rgba(228, 201, 132, 0.1)',
                  }
                },
                '& .MuiButton-root.Mui-selected': {
                  backgroundColor: '#E4C984',
                  color: '#fff',
                  borderColor: '#E4C984',
                  '&:hover': {
                    backgroundColor: '#d4b874',
                  }
                }
              }}
            >
              <Button
                className={guestFilter === 'upcoming' ? 'Mui-selected' : ''}
                onClick={() => setGuestFilter('upcoming')}
              >
                Upcoming ({apiCounts.upcoming})
              </Button>
              <Button
                className={guestFilter === 'inhouse' ? 'Mui-selected' : ''}
                onClick={() => setGuestFilter('inhouse')}
              >
                In-house ({apiCounts.inHouse})
              </Button>
            </ButtonGroup>
          </div>

          <div className="mb-4">
            <Autocomplete
              options={guests}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.guestName || option.label || '';
              }}
              value={guestName && guestId ? (guests.find(g => g.guestName === guestName || g.guestId === guestId) || null) : null}
              onChange={handleGuestSelect}
              renderInput={(params) => (
                <TextField {...params} label="Guest Name *" variant="outlined" size="small" />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                const dateRange = formatDateRange(option);
                const etaEtd = [
                  option.eta ? `ETA: ${option.eta}` : '',
                  option.etd ? `ETD: ${option.etd}` : ''
                ].filter(Boolean).join(' • ');
                return (
                  <li {...props} key={key || option.guestId || option.guestName} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'flex-start', textAlign: 'left' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'left', width: '100%' }}>
                        {option.guestName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'left', width: '100%' }}>
                        {option.guestId}{option.room ? ` • Room ${option.room}` : ''}{dateRange ? ` • ${dateRange}` : ''}
                      </Typography>
                      {etaEtd && (
                        <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 500, textAlign: 'left', width: '100%' }}>
                          {etaEtd}
                        </Typography>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedGuestForInfo(option);
                        setGuestInfoDialogOpen(true);
                      }}
                      sx={{ ml: 1, flexShrink: 0 }}
                    >
                      <InfoOutlinedIcon fontSize="small" sx={{ color: '#E4C984' }} />
                    </IconButton>
                  </li>
                );
              }}
              freeSolo
              onInputChange={(event, newInputValue) => {
                setGuestName(newInputValue);
              }}
            />
          </div>

          {/* Guest ID, Gender, and Room - Single Row */}
          <div className="mb-4" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr', gap: '12px' }}>
            {/* Guest ID */}
            <TextField
              label="Guest ID *"
              variant="outlined"
              value={guestId}
              onChange={(e) => setGuestId(e.target.value)}
              size="small"
            />

            {/* Guest Gender */}
            <TextField
              select
              label="Gender *"
              variant="outlined"
              value={guestGender}
              onChange={(e) => setGuestGender(e.target.value)}
              size="small"
            >
              <MenuItem value="">Not Specified</MenuItem>
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>

            {/* Guest Room */}
            <TextField
              label="Room"
              variant="outlined"
              value={guestRoom}
              onChange={handleGuestRoomChange}
              placeholder="e.g., 241"
              size="small"
              InputProps={{
                endAdornment: guestRoomTherapyAvailable && (
                  <span className="text-green-600 text-xs font-medium ml-2">
                    ✅
                  </span>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: guestRoomTherapyAvailable ? '#f0fdf4' : '#ffffff'
                }
              }}
            />
          </div>

          {/* Check-in/Check-out Info Panel - Only show when guest is selected */}
          {guestId && (guestArrivalDate || guestDepartureDate) && (
            <div className="mb-4 p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-amber-700 font-semibold text-sm">🗓️ Stay Details</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {/* Check-in */}
                <div className="bg-white/60 rounded p-2">
                  <div className="text-gray-500 text-xs font-medium mb-1">CHECK-IN</div>
                  <div className="font-medium text-gray-800">
                    {guestArrivalDate ? new Date(guestArrivalDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '-'}
                  </div>
                  {guestCheckinETA && (
                    <div className="text-amber-700 text-xs mt-0.5">
                      ETA: {guestCheckinETA}
                    </div>
                  )}
                </div>
                {/* Check-out */}
                <div className="bg-white/60 rounded p-2">
                  <div className="text-gray-500 text-xs font-medium mb-1">CHECK-OUT</div>
                  <div className="font-medium text-gray-800">
                    {guestDepartureDate ? new Date(guestDepartureDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '-'}
                  </div>
                  {guestCheckoutETD && (
                    <div className="text-orange-700 text-xs mt-0.5">
                      ETD: {guestCheckoutETD}
                    </div>
                  )}
                </div>
              </div>
              {/* Scheduling Notes */}
              <div className="mt-2 pt-2 border-t border-amber-200/50 text-xs text-gray-600">
                <div className="flex items-center gap-1">
                  <span className="text-amber-500">ℹ️</span>
                  <span>Day 1: Check-in day adjustments • Last Day: No treatment restrictions</span>
                </div>
              </div>
            </div>
          )}

          <Divider sx={{ my: 3 }} />

          {/* STEP 2: Package Info (Auto-filled or Manual) */}
          {showAssignedPackageReadonly ? (
            // Established stay — package change only; nights and dates stay fixed
            <>
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Inventory2OutlinedIcon sx={{ fontSize: 20, color: '#171717' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#171717' }}>
                      {packageLocked ? 'ASSIGNED PACKAGE' : 'SELECTED PACKAGE'}
                    </Typography>
                  </div>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowPackageDropdown(true)}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Change
                  </Button>
                </div>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {activePackage.packageName || activePackage.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {activePackage.nights}
                </Typography>
              </div>

              <div className="mb-4">
                <TextField
                  fullWidth
                  type="number"
                  label="Number of Nights"
                  variant="outlined"
                  value={nightsNumber}
                  disabled
                  helperText="Based on guest stay — unchanged when switching package"
                />
              </div>
            </>
          ) : showPackageChangeOnlyDropdown ? (
            // Change package only (nights and dates stay unchanged)
            <>
              <div className="mb-4">
                <Autocomplete
                  options={[...packages].sort((a, b) => {
                    const nameCompare = (a.packageName || a.name || '').localeCompare(b.packageName || b.name || '');
                    if (nameCompare !== 0) return nameCompare;
                    return (a.nightsNumber || 0) - (b.nightsNumber || 0);
                  })}
                  getOptionLabel={(option) => {
                    const name = option.packageName || option.name || '';
                    const nights = option.nights || (option.nightsNumber ? `${option.nightsNumber}N` : '');
                    return name ? `${name} (${nights})` : '';
                  }}
                  value={selectedPackage}
                  onChange={handlePackageChangeOnly}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Change package"
                      variant="outlined"
                      helperText="Nights and stay dates will not change. Regenerate the schedule after selecting."
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    (option.recordId || option.id) === (value?.recordId || value?.id)
                  }
                />
              </div>
              <div className="mb-4">
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setShowPackageDropdown(false)}
                  sx={{ textTransform: 'none' }}
                >
                  Cancel
                </Button>
              </div>
              <div className="mb-4">
                <TextField
                  fullWidth
                  type="number"
                  label="Number of Nights"
                  variant="outlined"
                  value={nightsNumber}
                  disabled
                  helperText="Unchanged when switching package"
                />
              </div>
            </>
          ) : (
            // Show package dropdown
            <>
              <div className="mb-4">
                <Autocomplete
                  options={[...packages].sort((a, b) => {
                    // Sort by name first, then by nights
                    const nameCompare = (a.packageName || '').localeCompare(b.packageName || '');
                    if (nameCompare !== 0) return nameCompare;
                    return (a.nightsNumber || 0) - (b.nightsNumber || 0);
                  })}
                  getOptionLabel={(option) => option.packageName ? `${option.packageName} (${option.nights || option.nightsNumber + 'N'})` : ''}
                  value={selectedPackage}
                  onChange={handlePackageSelect}
                  disabled={packageLocked}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Package *" variant="outlined" />
                  )}
                  isOptionEqualToValue={(option, value) => option.recordId === value.recordId}
                />
              </div>

              {/* Nights Display - Fixed duration for packages */}
              {selectedPackage && (
                <div className="mb-4">
                  <TextField
                    fullWidth
                    type="number"
                    label="Number of Nights *"
                    variant="outlined"
                    value={nightsNumber}
                    disabled
                    inputProps={{ min: 1, max: 50, step: 1 }}
                    helperText={`Fixed duration for this package: ${selectedPackage.nights}`}
                  />
                </div>
              )}
            </>
          )}

          {/* STEP 3: Wellness Advisor (Auto-filled or Manual) */}
          {assignedAdvisor && !showAdvisorDropdown && wellnessAdvisors.some((wa) => wa.name === assignedAdvisor) ? (
            // Show assigned advisor with option to change
            <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <PersonOutlineOutlinedIcon sx={{ fontSize: 20, color: '#171717' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#171717' }}>
                    WELLNESS ADVISOR
                  </Typography>
                </div>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowAdvisorDropdown(true)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Change
                </Button>
              </div>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {assignedAdvisor}
              </Typography>
            </div>
          ) : (
            // Show advisor dropdown
            <div className="mb-4">
              <Autocomplete
                options={[...wellnessAdvisors].sort((a, b) => (a.name || '').localeCompare(b.name || ''))}
                getOptionLabel={(option) => option.name || option}
                value={wellnessAdvisors.find(wa => wa.name === assignedDoctor) || null}
                onChange={(event, newValue) => {
                  const newDoctorName = newValue ? newValue.name : '';
                  const previousDoctor = assignedDoctor;
                  
                  setAssignedDoctor(newDoctorName);
                  if (newValue) setAssignedAdvisor(newValue.name);
                  
                  // Notify AIIMS if doctor changed and we have guest info
                  if (newDoctorName && newDoctorName !== previousDoctor && guestId) {
                    console.log('🔍 AIIMS Webhook Debug:', {
                      doctorName: newDoctorName,
                      guestId: guestId,
                      externalBookingId: externalBookingId
                    });
                    notifyAIMSDoctorAssignment(newDoctorName, guestId, externalBookingId);
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Wellness Advisor *" variant="outlined" />
                )}
                freeSolo
                onInputChange={(event, newInputValue) => {
                  if (event && event.type === 'change') {
                    setAssignedDoctor(newInputValue);
                  }
                }}
              />
            </div>
          )}

          {/* Start Date */}
          <div className="mb-4">
            <TextField
              fullWidth
              label="Start Date *"
              type="date"
              variant="outlined"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={stayDatesLocked}
              InputLabelProps={{ shrink: true }}
              helperText={
                stayDatesLocked
                  ? 'Synced to guest check-in date from CRM — updates when guest arrival changes'
                  : undefined
              }
            />
          </div>

          {/* General Instruction */}
          <div className="mb-4">
            <TextField
              fullWidth
              label="General Instruction (Optional)"
              variant="outlined"
              multiline
              rows={3}
              value={generalInstruction}
              onChange={(e) => setGeneralInstruction(e.target.value)}
              placeholder="e.g., Recently came out of surgery, avoid high pressure treatments..."
              helperText="This applies to all treatments in the schedule"
            />
          </div>

          {/* Booking Preferences Section */}
          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>
              BOOKING PREFERENCES
            </Typography>
          </Divider>

          {/* Therapist Gender Preference */}
          <div className="mb-4">
            <FormControl fullWidth variant="outlined">
              <InputLabel>Therapist Gender Preference</InputLabel>
              <Select
                value={therapistGenderPreference}
                onChange={(e) => setTherapistGenderPreference(e.target.value)}
                label="Therapist Gender Preference"
              >
                <MenuItem value="">No Preference</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block', 
                mt: 0.5, 
                color: '#f59e0b',
                fontSize: '0.75rem',
                lineHeight: 1.4
              }}
            >
              ⚠️ For gender-sensitive treatments (massage, physical exams, etc.), therapist gender will automatically match guest gender for safety and compliance, regardless of this preference.
            </Typography>
          </div>

          {/* Therapist Level */}
          <div className="mb-4">
            <FormControl fullWidth variant="outlined">
              <InputLabel>Therapist Level</InputLabel>
              <Select
                value={therapistLevel}
                onChange={(e) => setTherapistLevel(e.target.value)}
                label="Therapist Level"
              >
                <MenuItem value="">Any Level</MenuItem>
                <MenuItem value="Senior">Senior</MenuItem>
                <MenuItem value="Expert">Expert</MenuItem>
                <MenuItem value="Master">Master</MenuItem>
              </Select>
            </FormControl>
          </div>

          {/* Room Preference */}
          <div className="mb-4">
            <Autocomplete
              options={rooms}
              getOptionLabel={(option) => {
                const label = option.name || '';
                const location = option.location || '';
                if (location && location !== 'Wellness Pavilion') {
                  return `${label} (${location})`;
                }
                return label;
              }}
              getOptionDisabled={(option) => {
                // Disable guest-specific rooms that don't match the current guest's room
                const location = option.location || '';
                if (location.startsWith('Room No:')) {
                  const roomNumber = location.replace('Room No:', '').trim();
                  // This room is only for guests in that specific room
                  if (guestRoom && guestRoom !== roomNumber) {
                    return true; // Disable this option
                  }
                }
                return false; // Enable all other rooms
              }}
              value={roomPreference}
              onChange={(event, newValue) => {
                // Additional validation on change
                const location = newValue?.location || '';
                if (location.startsWith('Room No:')) {
                  const roomNumber = location.replace('Room No:', '').trim();
                  if (guestRoom && guestRoom !== roomNumber) {
                    console.warn(`Cannot select room ${newValue?.name} - only for guests in room ${roomNumber}`);
                    return; // Don't set the preference
                  }
                }
                setRoomPreference(newValue);
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Room Preference" 
                  variant="outlined"
                  helperText={
                    guestRoomTherapyAvailable 
                      ? "✅ Guest's in-room therapy will be prioritized"
                      : "Common therapy rooms will be used"
                  }
                />
              )}
              renderOption={(props, option) => {
                const location = option.location || '';
                const isGuestRoom = location.startsWith('Room No:');
                const roomNumber = isGuestRoom ? location.replace('Room No:', '').trim() : '';
                const isForThisGuest = isGuestRoom && guestRoom && guestRoom === roomNumber;
                const isDisabled = isGuestRoom && guestRoom && guestRoom !== roomNumber;
                
                return (
                  <li {...props} style={{ 
                    opacity: isDisabled ? 0.5 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                  }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.name} {isForThisGuest && '🏠'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDisabled ? 'error.main' : 'text.secondary' }}>
                        {location || 'Common area'}
                        {isDisabled && ' (Only for guests in this room)'}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
            />
          </div>

          {/* Package Description */}
          {selectedPackage && selectedPackage.packageDescription && (
            <div className="mb-4 p-3 bg-gray-50 rounded">
              <p className="text-sm text-gray-600">{selectedPackage.packageDescription}</p>
            </div>
          )}

          {/* Generate Button */}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => {
              console.log('Generate Schedule - Current State:', {
                scheduleLoading,
                selectedPackage: selectedPackage?.packageName || null,
                assignedPackage: assignedPackage?.name || null,
                selectedNights,
                nightsNumber,
                guestName,
                guestId,
                assignedDoctor,
                startDate,
                isDisabled: scheduleLoading || 
                  (!selectedPackage && !assignedPackage) || 
                  !selectedNights || 
                  !guestName || 
                  !guestId || 
                  !assignedDoctor || 
                  !startDate
              });
              handleGenerateSchedule();
            }}
            disabled={
              scheduleLoading || 
              (!selectedPackage && !assignedPackage) || 
              !selectedNights || 
              !guestName || 
              !guestId || 
              !assignedDoctor || 
              !startDate
            }
            style={{
              backgroundColor: '#E4C984',
              color: '#171717',
              fontWeight: 'bold',
              padding: '12px',
              marginTop: '16px'
            }}
          >
            {scheduleLoading ? <CircularProgress size={24} /> : 'Generate Schedule'}
          </Button>

          {/* Debug Info - Remove after testing */}
          <div style={{ fontSize: '10px', marginTop: '8px', color: '#666', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            <div>Package: {selectedPackage?.packageName || assignedPackage?.name || 'None'} ✓</div>
            <div>Nights: {selectedNights || 'Not set'} {selectedNights ? '✓' : '✗'}</div>
            <div>Guest: {guestName || 'Not set'} {guestName ? '✓' : '✗'}</div>
            <div>Guest ID: {guestId || 'Not set'} {guestId ? '✓' : '✗'}</div>
            <div>Advisor: {assignedDoctor || 'Not set'} {assignedDoctor ? '✓' : '✗'}</div>
            <div>Start Date: {startDate || 'Not set'} {startDate ? '✓' : '✗'}</div>
          </div>

          {scheduleError && (
            <Alert severity="error" className="mt-4">
              {scheduleError}
            </Alert>
          )}
        </div>
      </div>

      {/* Right Panel - Schedule Preview */}
      <div className="flex-1 min-w-0 m-4 bg-white rounded-lg shadow-lg overflow-auto">
        <div className="p-6">
          {schedule ? (
            <>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold">Schedule Preview</h3>
                  
                  <div className="flex gap-2">
                    {/* Add/Remove Day Buttons */}
                    <ButtonGroup size="small" aria-label="manage days">
                      <Button
                        onClick={handleAddDay}
                        startIcon={<AddCircleOutlineIcon />}
                        sx={{
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          '&:hover': { backgroundColor: '#45a049' }
                        }}
                        title="Add a new day to the end of schedule"
                      >
                        Add Day
                      </Button>
                      <Button
                        onClick={handleRemoveLastDay}
                        sx={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          '&:hover': { backgroundColor: '#d32f2f' }
                        }}
                        title="Remove the last day from schedule"
                      >
                        Remove Day
                      </Button>
                    </ButtonGroup>
                    <Button
                      onClick={handleDeleteItinerary}
                      size="small"
                      sx={{
                        backgroundColor: '#171717',
                        color: 'white',
                        '&:hover': { backgroundColor: '#333' }
                      }}
                      title="Delete the full itinerary and its bookings"
                    >
                      Delete Itinerary
                    </Button>
                    
                    {/* View Mode Toggle */}
                    <ButtonGroup size="small" aria-label="view mode toggle">
                    <Button
                      variant={viewMode === 'list' ? 'contained' : 'outlined'}
                      onClick={() => setViewMode('list')}
                      startIcon={<ViewListIcon />}
                      sx={{
                        backgroundColor: viewMode === 'list' ? '#E4C984' : 'transparent',
                        color: viewMode === 'list' ? '#171717' : '#666',
                        borderColor: '#E4C984',
                        '&:hover': {
                          backgroundColor: viewMode === 'list' ? '#d4b974' : 'rgba(228, 201, 132, 0.08)',
                          borderColor: '#E4C984'
                        }
                      }}
                    >
                      List
                    </Button>
                    <Button
                      variant={viewMode === 'table' ? 'contained' : 'outlined'}
                      onClick={() => setViewMode('table')}
                      startIcon={<TableRowsIcon />}
                      sx={{
                        backgroundColor: viewMode === 'table' ? '#E4C984' : 'transparent',
                        color: viewMode === 'table' ? '#171717' : '#666',
                        borderColor: '#E4C984',
                        '&:hover': {
                          backgroundColor: viewMode === 'table' ? '#d4b974' : 'rgba(228, 201, 132, 0.08)',
                          borderColor: '#E4C984'
                        }
                      }}
                    >
                      Table
                    </Button>
                    <Button
                      variant={viewMode === 'calendar' ? 'contained' : 'outlined'}
                      onClick={() => setViewMode('calendar')}
                      startIcon={<CalendarViewMonthIcon />}
                      sx={{
                        backgroundColor: viewMode === 'calendar' ? '#E4C984' : 'transparent',
                        color: viewMode === 'calendar' ? '#171717' : '#666',
                        borderColor: '#E4C984',
                        '&:hover': {
                          backgroundColor: viewMode === 'calendar' ? '#d4b974' : 'rgba(228, 201, 132, 0.08)',
                          borderColor: '#E4C984'
                        }
                      }}
                    >
                      Calendar
                    </Button>
                  </ButtonGroup>
                  </div>
                </div>
                
                <div className="text-sm text-gray-600 mb-3">
                  <p><strong>Guest:</strong> {schedule.guestName} (ID: {schedule.guestId})</p>
                  <p><strong>Package:</strong> {schedule.package} - {schedule.nights}</p>
                  <p><strong>Wellness Advisor:</strong> {schedule.assignedDoctor}</p>
                  <p><strong>Total Treatments:</strong> {schedule.totalTreatments}</p>
                </div>

                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    border: '1px solid #fde68a',
                    borderRadius: 1,
                    bgcolor: '#fffbeb',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                    gap: 2,
                  }}
                >
                  <TextField
                    size="small"
                    type="date"
                    label="Package start date"
                    value={startDate}
                    onChange={(e) => handleShiftPackageStartDate(e.target.value)}
                    inputProps={{
                      min: getPackageStartDateBounds().minDate,
                      max: getPackageStartDateBounds().maxDate,
                    }}
                    InputLabelProps={{ shrink: true }}
                    helperText="Any day from check-in through checkout — shifts all treatment dates together"
                    sx={{ minWidth: 220 }}
                  />
                  {guestArrivalDate && guestDepartureDate && (
                    <Typography variant="caption" color="text.secondary" sx={{ pb: 0.5 }}>
                      Stay: {guestArrivalDate} → {getCheckOutISO({ checkOut: guestDepartureDate, departureDate: guestDepartureDate }) || guestDepartureDate}
                    </Typography>
                  )}
                </Box>

                {/* General Instruction */}
                {generalInstruction && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded">
                    <div className="text-xs font-semibold text-amber-900 mb-1">GENERAL INSTRUCTION:</div>
                    <div className="text-sm text-amber-800">{generalInstruction}</div>
                  </div>
                )}

                {/* Treatment Type Summary */}
                {(() => {
                  const typeCounts = {};
                  schedule.schedule.forEach(day => {
                    day.treatments.forEach(treatment => {
                      const type = treatment.type || 'OTHER';
                      typeCounts[type] = (typeCounts[type] || 0) + 1;
                    });
                  });
                  
                  return Object.keys(typeCounts).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200">
                      {Object.entries(typeCounts).map(([type, count]) => (
                        <span 
                          key={type} 
                          className={`text-xs px-3 py-1 rounded-full font-medium ${getTypeColor(type)}`}
                        >
                          {type}: {count}
                        </span>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* LIST VIEW - Schedule by Day */}
              {viewMode === 'list' && (
                <div className="space-y-4">
                  {schedule.schedule.map((day, dayIndex) => (
                  <div key={day.day} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-bold">
                        Day {day.day} - {day.dayOfWeek}
                      </h4>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">{day.date}</span>
                        {/* Add Treatment Button */}
                        <IconButton
                          size="small"
                          onClick={() => handleAddTreatment(dayIndex)}
                          sx={{
                            color: '#E4C984',
                            '&:hover': { backgroundColor: 'rgba(228, 201, 132, 0.1)' }
                          }}
                          title="Add treatment to this day"
                        >
                          <AddCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {[...day.treatments]
                        .map(treatment => {
                          // Enrich treatment with booking time data for sorting
                          const booking = bookingResults?.bookings?.find((b) => {
                            if (b.date !== day.date) return false;
                            if (treatment.inclusionId && b.inclusionId) {
                              return b.inclusionId === treatment.inclusionId;
                            }
                            return b.treatmentName === treatment.name;
                          });
                          return {
                            ...treatment,
                            startTime: booking?.startTime || treatment.startTime,
                            endTime: booking?.endTime || treatment.endTime
                          };
                        })
                        .sort((a, b) => {
                          // Sort by startTime if both have it (chronological order)
                          if (a.startTime && b.startTime) {
                            // Handle both string format (HH:MM) and number format (minutes)
                            const getMinutes = (time) => {
                              if (typeof time === 'number') return time;
                              if (typeof time === 'string') {
                                const [hours, mins] = time.split(':').map(Number);
                                return hours * 60 + mins;
                              }
                              return 0;
                            };
                            return getMinutes(a.startTime) - getMinutes(b.startTime);
                          }
                          // If only one has startTime, put it first
                          if (a.startTime) return -1;
                          if (b.startTime) return 1;
                          // Otherwise sort by order number
                          return (a.order || 0) - (b.order || 0);
                        })
                        .map((treatment, sortedIdx) => {
                        // CRITICAL: List is sorted by booked startTime; schedule mutations use day.treatments[index].
                        // Resolve stable index in the unsorted array (sortedIdx ≠ row index after reorder).
                        let rowTreatmentIndex = day.treatments.findIndex(
                          (t) => treatment.inclusionId && t.inclusionId === treatment.inclusionId
                        );
                        if (rowTreatmentIndex < 0) {
                          rowTreatmentIndex = day.treatments.findIndex(
                            (t) =>
                              t.name === treatment.name &&
                              Number(t.order ?? 0) === Number(treatment.order ?? 0)
                          );
                        }
                        if (rowTreatmentIndex < 0) {
                          rowTreatmentIndex = sortedIdx;
                        }

                        // Match bookings/failures by inclusionId when present (duplicate names on same day)
                        const booking = bookingResults?.bookings?.find((b) => {
                          if (b.date !== day.date) return false;
                          if (treatment.inclusionId && b.inclusionId) {
                            return b.inclusionId === treatment.inclusionId;
                          }
                          return b.treatmentName === treatment.name;
                        });
                        const failure = bookingResults?.failures?.find((f) => {
                          if (f.date !== day.date) return false;
                          if (treatment.inclusionId && f.inclusionId) {
                            return f.inclusionId === treatment.inclusionId;
                          }
                          return f.treatmentName === treatment.name;
                        });
                        const isBooked = !!booking;
                        const isFailed = !!failure;
                        const failureReason = failure?.reason || '';
                        const failureType = failure?.type || '';
                        const isEditing =
                          editingTreatment?.dayIndex === dayIndex &&
                          editingTreatment?.treatmentIndex === rowTreatmentIndex;

                        return (
                          <div 
                            key={treatment.inclusionId || rowTreatmentIndex} 
                            className={`border rounded p-3 hover:shadow-sm transition-shadow ${
                              isBooked ? 'border-green-400 bg-green-50' : 
                              isEditing ? 'border-yellow-400 bg-yellow-50' :
                              'border-gray-200 bg-white'
                            }`}
                          >
                            {/* Treatment Header or Edit Mode */}
                            {isEditing ? (
                              <div className="mb-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#F59E0B' }}>
                                    ✏️ EDITING
                                  </Typography>
                                </div>
                                
                                {/* Treatment Selection */}
                                <Autocomplete
                                  key={`treatment-${dayIndex}-${rowTreatmentIndex}-${treatment.name}`}  // Force re-render when treatment name changes
                                  size="small"
                                options={availableTreatments}  // Show all treatments to allow upgrades
                                  getOptionLabel={(opt) => `${opt.name} (${opt.duration} min)`}
                                  value={availableTreatments.find(t => t.name === treatment.name) || null}
                                  onChange={(e, newValue) => {
                                    if (newValue) {
                                      console.log('🔄 Treatment change detected:', {
                                        from: treatment.name,
                                        to: newValue.name,
                                        isManuallyAdded: treatment.isManuallyAdded,
                                        hasName: !!treatment.name
                                      });
                                      
                                      // Check if this is a manually added treatment (new standalone service)
                                      if (treatment.isManuallyAdded && !treatment.name) {
                                        console.log('➡️ This is a NEW manually added treatment → show standalone modal');
                                        // This is the first time selecting a treatment for a manually added one
                                        // Show approval modal
                                        setStandaloneTreatmentData({
                                          dayIndex,
                                          treatmentIndex: rowTreatmentIndex,
                                          treatment: newValue,
                                          date: day.date
                                        });
                                        setShowStandaloneApprovalModal(true);
                                      } else if (treatment.isDailyInclusion) {
                                        // NEW: Treat replacement of daily inclusion placeholder as standalone
                                        console.log('➡️ This is a Daily Inclusion placeholder being replaced → show standalone modal');
                                        setStandaloneTreatmentData({
                                          dayIndex,
                                          treatmentIndex: rowTreatmentIndex,
                                          treatment: newValue,
                                          date: day.date
                                        });
                                        setShowStandaloneApprovalModal(true);
                                      } else {
                                        console.log('➡️ Checking if this is an upgrade...');
                                        // Check if this is an upgrade (different tier)
                                        const currentTier = treatment.tier_level;
                                        const newTier = newValue.tier_level;
                                        
                                        console.log('  Current tier:', currentTier, 'New tier:', newTier);
                                        
                                        // Tier comparison: T1 < T2 < T3
                                        const tierToNumber = (tier) => {
                                          if (!tier) return 0;
                                          const match = tier.match(/T(\d+)/i);
                                          return match ? parseInt(match[1]) : 0;
                                        };
                                        
                                        const currentTierNum = tierToNumber(currentTier);
                                        const newTierNum = tierToNumber(newTier);
                                        
                                        console.log('  Tier numbers: current =', currentTierNum, ', new =', newTierNum);
                                        
                                        // CRITICAL: Check if tier level increases - if yes, call backend API to calculate upgrade
                                        if (newTierNum > currentTierNum && newTierNum > 0 && treatment.name && newValue.name) {
                                          // Potential upgrade - call backend API for accurate calculation
                                          console.log(`🔼 Potential upgrade detected: ${currentTier || 'null'} → ${newTier}, calling backend API...`);
                                          
                                          // Call backend API to calculate upgrade amount
                                          apiPost('/api/billing/treatment-upgrade-preview', {
                                            original_treatment_name: treatment.name,
                                            new_treatment_name: newValue.name
                                          })
                                          .then(response => {
                                            if (!response.ok) {
                                              throw new Error('Upgrade preview API failed');
                                            }
                                            return response.json();
                                          })
                                          .then(upgradeData => {
                                            console.log('✅ Backend upgrade preview:', upgradeData);
                                            
                                            if (upgradeData.is_upgrade && parseFloat(upgradeData.upgrade_amount) > 0) {
                                              // This is a REAL upgrade - show modal
                                              console.log(`🔼 ✅ UPGRADE CONFIRMED → SHOWING MODAL: ${upgradeData.original_tier_level} (₹${upgradeData.original_tier_value}) → ${upgradeData.new_tier_level} (₹${upgradeData.new_tier_value}), upgrade amount: ₹${upgradeData.upgrade_amount}`);
                                              
                                              // Use backend-calculated preview data
                                              const preview = {
                                                original_treatment_name: upgradeData.original_treatment_name,
                                                original_tier_level: upgradeData.original_tier_level || 'None',
                                                original_tier_value: parseFloat(upgradeData.original_tier_value),
                                                new_treatment_name: upgradeData.new_treatment_name,
                                                new_tier_level: upgradeData.new_tier_level,
                                                new_tier_value: parseFloat(upgradeData.new_tier_value),
                                                upgrade_amount: parseFloat(upgradeData.upgrade_amount),
                                                tax_rate: parseFloat(upgradeData.tax_rate),
                                                tax_on_upgrade: parseFloat(upgradeData.tax_on_upgrade),
                                                total_additional_cost: parseFloat(upgradeData.total_additional_cost)
                                              };
                                              
                                              setUpgradePreview(preview);
                                              setPendingUpgradeChange({
                                                dayIndex,
                                                treatmentIndex: rowTreatmentIndex,
                                                originalTreatmentName: treatment.name,
                                                originalTreatment: { 
                                                  name: treatment.name,
                                                  tier_level: treatment.tier_level,
                                                  tier_value: parseFloat(upgradeData.original_tier_value)
                                                },
                                                newTreatment: {
                                                  name: newValue.name,
                                                  duration: newValue.duration,
                                                  prepTime: newValue.preparation_time || 10,
                                                  cleanupTime: newValue.cleanup_time || 15,
                                                  type: newValue.type,
                                                  tier_level: newValue.tier_level
                                                },
                                                originalTierValue: parseFloat(upgradeData.original_tier_value)
                                              });
                                              setShowUpgradeModal(true);
                                            } else {
                                              // Not an upgrade (same price, downgrade, etc.) - just change treatment
                                              console.log(`↔️ ${upgradeData.message || 'No upgrade'} - applying change directly`);
                                              handleTreatmentChange(dayIndex, rowTreatmentIndex, {
                                                name: newValue.name,
                                                duration: newValue.duration,
                                                prepTime: newValue.preparation_time || 10,
                                                cleanupTime: newValue.cleanup_time || 15,
                                                type: newValue.type,
                                                tier_level: newValue.tier_level
                                              });
                                            }
                                          })
                                          .catch(error => {
                                            console.error('❌ Upgrade preview API error:', error);
                                            // Fallback: apply change directly without upgrade modal
                                            console.log('⚠️ Falling back to direct treatment change due to API error');
                                            handleTreatmentChange(dayIndex, rowTreatmentIndex, {
                                              name: newValue.name,
                                              duration: newValue.duration,
                                              prepTime: newValue.preparation_time || 10,
                                              cleanupTime: newValue.cleanup_time || 15,
                                              type: newValue.type,
                                              tier_level: newValue.tier_level
                                            });
                                          });
                                        } else {
                                          // Regular treatment change (same tier, downgrade, or no tier change)
                                          console.log(`↔️ No upgrade needed (tier: ${currentTier} → ${newTier}) - applying change directly`);
                                          handleTreatmentChange(dayIndex, rowTreatmentIndex, {
                                            name: newValue.name,
                                            duration: newValue.duration,
                                            prepTime: newValue.preparation_time || 10,
                                            cleanupTime: newValue.cleanup_time || 15,
                                            type: newValue.type,
                                            tier_level: newValue.tier_level
                                          });
                                        }
                                      }
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField {...params} label={`Select Treatment ${treatment.tier_level ? `(Tier: ${treatment.tier_level})` : ''}`} size="small" />
                                  )}
                                  sx={{ mb: 1.5 }}
                                />

                                {isScheduleDateEditableTreatment(treatment, availableTreatments) && (() => {
                                  const { minDate, maxDate } = getConsultationDateBounds();
                                  return (
                                    <TextField
                                      fullWidth
                                      size="small"
                                      type="date"
                                      label={getScheduleDateEditLabel(treatment, availableTreatments)}
                                      value={day.date}
                                      onChange={(e) =>
                                        handleConsultationDateChange(
                                          dayIndex,
                                          rowTreatmentIndex,
                                          e.target.value
                                        )
                                      }
                                      inputProps={{ min: minDate, max: maxDate }}
                                      InputLabelProps={{ shrink: true }}
                                      helperText={getScheduleDateEditHelperText(treatment, availableTreatments)}
                                      sx={{ mb: 1.5 }}
                                    />
                                  );
                                })()}
                                
                                {/* Duration Override */}
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Duration (minutes)"
                                  value={treatment.duration}
                                  onChange={(e) => handleDurationChange(dayIndex, rowTreatmentIndex, e.target.value)}
                                  inputProps={{ min: 15, max: 240, step: 5 }}
                                  helperText="Adjust duration if needed (15-240 min)"
                                  sx={{ mb: 1.5 }}
                                />
                                
                                {/* Action Buttons */}
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button 
                                      size="small" 
                                      variant="contained"
                                      onClick={() => saveTreatmentEdit(dayIndex, rowTreatmentIndex)}
                                      sx={{ 
                                        backgroundColor: '#E4C984', 
                                        color: '#171717',
                                        '&:hover': { backgroundColor: '#cbb16f' }
                                      }}
                                    >
                                      Save
                                    </Button>
                                    <Button 
                                      size="small" 
                                      variant="outlined"
                                      onClick={() => cancelTreatmentEdit()}
                                    >
                                      Cancel
                                    </Button>
                                    {!isBooked && treatment.name && (
                                      <Button 
                                        size="small" 
                                        variant="contained"
                                        onClick={() => handleRetryBooking(dayIndex, rowTreatmentIndex, treatment, day.date)}
                                        sx={{ 
                                          backgroundColor: '#E4C984',
                                          color: '#171717',
                                          '&:hover': { backgroundColor: '#cbb16f' }
                                        }}
                                      >
                                        🔄 Retry
                                      </Button>
                                    )}
                                  </Box>
                                  {treatment.isManuallyAdded && (
                                    <Button 
                                      size="small" 
                                      variant="outlined"
                                      color="error"
                                      onClick={() => handleDeleteTreatment(dayIndex, rowTreatmentIndex)}
                                    >
                                      Delete
                                    </Button>
                                  )}
                                </Box>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <div className="font-medium">
                                      {treatment.name}
                                      {(() => {
                                        const treatmentDetails = availableTreatments.find(t => t.name === treatment.name);
                                        const isGenderSensitive = treatmentDetails?.gender_specific?.toUpperCase() === 'YES';
                                        return isGenderSensitive ? (
                                          <span 
                                            title="Gender-sensitive treatment - therapist will match guest gender" 
                                            style={{ marginLeft: '6px', fontSize: '0.9em' }}
                                          >
                                            🚻
                                          </span>
                                        ) : null;
                                      })()}
                                    </div>
                                    {treatment.isManuallyAdded && (
                                      <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-purple-100 text-purple-700 border border-purple-300">
                                        STANDALONE
                                      </span>
                                    )}
                                    {(treatment.isUpgraded || upgradeApprovals[treatment.inclusionId]) && (
                                      <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-amber-100 text-amber-700 border border-amber-300">
                                        UPGRADE
                                      </span>
                                    )}
                                    {/* Debug: Show when upgrade badge should appear */}
                                    {false && console.log('Treatment:', treatment.name, 'isUpgraded:', treatment.isUpgraded, 'inclusionId:', treatment.inclusionId, 'hasUpgradeApproval:', !!upgradeApprovals[treatment.inclusionId])}
                                    {isBooked && (
                                      <span className="text-green-600 text-sm">✅ BOOKED</span>
                                    )}
                                  </div>
                                  
                                  {/* Booking Details - Compact single line */}
                                  {isBooked && booking && (
                                    <div className="mt-1 text-xs text-gray-700">
                                      <span className="font-medium">Time:</span> {formatTime(booking.startTime)}-{formatTime(booking.endTime)} • 
                                      <span className="font-medium"> Room:</span> {booking.room || 'N/A'} • 
                                      <span className="font-medium"> Therapist{booking.therapistsCount > 1 ? 's' : ''}:</span> {(() => {
                                        const therapists = booking.therapists || [];
                                        const therapistsCount = booking.therapistsCount || 1;
                                        if (therapistsCount > 1) {
                                          const allNames = therapists.map(t => t.name).join(', ');
                                          if (allNames.length < 60) {
                                            return `${allNames} (${therapistsCount} therapists)`;
                                          }
                                          const primary = therapists.find(t => t.is_primary);
                                          const primaryName = primary ? primary.name : booking.therapist;
                                          return `${primaryName} +(${therapistsCount - 1}) more`;
                                        }
                                        return booking.therapist || 'N/A';
                                      })()}
                                    </div>
                                  )}

                                  {/* Unbooked consultation — show expected doctor / pending state */}
                                  {!isBooked && isConsultationTreatment(treatment, availableTreatments) && (
                                    <div className="mt-1 text-xs text-gray-600">
                                      {assignedDoctor && wellnessAdvisors.some((wa) => wa.name === assignedDoctor) ? (
                                        <>
                                          <span className="font-medium">Assigned doctor:</span> {assignedDoctor}
                                          <span className="text-gray-500"> • Not yet scheduled</span>
                                        </>
                                      ) : (
                                        <span className="text-orange-700 font-medium">
                                          Select a valid Wellness Advisor to schedule this consultation
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Failure/Skip Reason - only after a book attempt */}
                                  {isFailed && failureReason && hasAttemptedBooking && (
                                    <div className={`mt-1 text-xs px-2 py-1 rounded ${
                                      failureType === 'arrival_day_skip' || failureType === 'departure_day_skip' 
                                        ? 'bg-orange-50 text-orange-700 border border-orange-200' 
                                        : 'bg-red-50 text-red-700 border border-red-200'
                                    }`}>
                                      <span className="font-medium">
                                        {failureType === 'arrival_day_skip' || failureType === 'departure_day_skip' ? '⏭️ Skipped: ' : '❌ Failed: '}
                                      </span>
                                      {failureReason}
                                    </div>
                                  )}
                                  
                                  <div className="flex gap-2 items-center flex-wrap mt-1">
                                    {treatment.type && (
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getTypeColor(treatment.type)}`}>
                                        {treatment.type}
                                      </span>
                                    )}
                                    {treatment.isDailyInclusion && (
                                      <span className="text-xs text-blue-600 italic">Daily Inclusion</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="text-sm text-gray-600 font-medium flex items-center gap-1">
                                    {/* Show custom duration if booking has it, otherwise treatment default */}
                                    {(() => {
                                      if (isBooked && booking && (booking.duration || booking.custom_duration)) {
                                        const customDur = booking.duration || booking.custom_duration;
                                        return (
                                          <>
                                            {customDur} min
                                            <span className="text-xs px-1.5 py-0.5 rounded-full font-bold bg-blue-100 text-blue-700 border border-blue-300">
                                              CUSTOM
                                            </span>
                                          </>
                                        );
                                      }
                                      return `${treatment.duration} min`;
                                    })()}
                                  </div>
                                  {/* Edit Button - Different behavior for booked vs unbooked */}
                                  {(() => {
                                    // Check if booking is editable (not completed or checked_in)
                                    const isEditableBooking = isBooked && booking && 
                                      booking.status !== 'completed' && 
                                      booking.status !== 'checked_in';
                                    
                                    // Show edit button if either:
                                    // 1. Not booked yet (inline editing)
                                    // 2. Booked but editable status (EditBookingModal)
                                    if (!isBooked || isEditableBooking) {
                                      return (
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            if (isBooked && booking) {
                                              // Open EditBookingModal for booked treatments
                                              console.log('Opening EditBookingModal for booking:', booking);
                                              
                                              // Enrich booking data with schedule context
                                              const pkgForEdit = selectedPackage || assignedPackage;
                                              const pkgIdForEdit = pkgForEdit?.id ?? pkgForEdit?.recordId ?? pkgForEdit?.programId ?? pkgForEdit?.programRecordId;
                                              const pkgNameForEdit = pkgForEdit?.name || pkgForEdit?.packageName || null;
                                              const enrichedBooking = {
                                                ...booking,
                                                date: day.date,
                                                treatment: treatment.name,
                                                inclusionId: treatment.inclusionId,
                                                // EditBookingModal: preserve package inclusion billing (not standalone add-ons)
                                                scheduleBillingIsPackageInclusion: !treatment.isManuallyAdded,
                                                package_name_for_billing: pkgNameForEdit,
                                                package_id_for_billing: pkgIdForEdit != null ? String(pkgIdForEdit) : null,
                                                // Ensure required fields are present
                                                guestId: booking.guestId || booking.guest_id || guestId,
                                                guestName: booking.guestName || booking.guest_name || guestName,
                                                guest_id: booking.guest_id || booking.guestId || guestId,
                                                guest_name: booking.guest_name || booking.guestName || guestName,
                                                guestRoom: booking.guestRoom || booking.guest_room || guestRoom || '',
                                                guest_room: booking.guest_room || booking.guestRoom || guestRoom || '',
                                                id: booking.bookingId || booking.id,
                                                bookingId: booking.bookingId || booking.id
                                              };
                                              
                                              console.log('Enriched booking for EditBookingModal:', enrichedBooking);
                                              setEditingBooking(enrichedBooking);
                                              setShowEditBookingModal(true);
                                            } else {
                                              // Keep inline editing for unbooked treatments
                                              startEditingTreatment(dayIndex, rowTreatmentIndex);
                                            }
                                          }}
                                          sx={{
                                            color: isBooked ? '#10b981' : '#E4C984', // Green for booked, gold for unbooked
                                            '&:hover': { 
                                              backgroundColor: isBooked ? 'rgba(16, 185, 129, 0.1)' : 'rgba(228, 201, 132, 0.1)' 
                                            }
                                          }}
                                          title={isBooked ? 'Edit booking' : 'Edit treatment'}
                                        >
                                          <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              </div>
                            )}

                            {/* Individual Instruction Field - Hide in edit mode */}
                            {!isEditing && (
                              <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                placeholder="Treatment-specific instructions (e.g., use less pressure, focus on lower back...)"
                                value={treatmentInstructions[treatment.inclusionId] || ''}
                                onChange={(e) => handleTreatmentInstructionChange(treatment.inclusionId, e.target.value)}
                                disabled={isBooked}
                                sx={{ 
                                  '& .MuiOutlinedInput-root': { 
                                    fontSize: '0.875rem',
                                    backgroundColor: isBooked ? '#f0fdf4' : '#fafafa'
                                  } 
                                }}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  ))}
                </div>
              )}

              {/* TABLE VIEW - Coming Soon */}
              {viewMode === 'table' && (
                <div className="mt-4">
                  <PackageSchedulingTableView
                    schedule={schedule}
                    bookingResults={bookingResults}
                    editingTreatment={editingTreatment}
                    setEditingTreatment={setEditingTreatment}
                    availableTreatments={availableTreatments}
                    handleTreatmentChange={handleTreatmentChange}
                    handleDurationChange={handleDurationChange}
                    handleConsultationDateChange={handleConsultationDateChange}
                    getConsultationDateBounds={getConsultationDateBounds}
                    getPackageStartDateBounds={getPackageStartDateBounds}
                    handleShiftPackageStartDate={handleShiftPackageStartDate}
                    startDate={startDate}
                    guestArrivalDate={guestArrivalDate}
                    guestDepartureDate={guestDepartureDate}
                    isConsultationTreatment={isConsultationTreatment}
                    isScheduleDateEditableTreatment={isScheduleDateEditableTreatment}
                    getScheduleDateEditLabel={getScheduleDateEditLabel}
                    getScheduleDateEditHelperText={getScheduleDateEditHelperText}
                    saveTreatmentEdit={saveTreatmentEdit}
                    cancelTreatmentEdit={cancelTreatmentEdit}
                    handleRetryBooking={handleRetryBooking}
                    handleDeleteTreatment={handleDeleteTreatment}
                    handleAddTreatment={handleAddTreatment}
                    treatmentInstructions={treatmentInstructions}
                    handleTreatmentInstructionChange={handleTreatmentInstructionChange}
                    setStandaloneTreatmentData={setStandaloneTreatmentData}
                    setShowStandaloneApprovalModal={setShowStandaloneApprovalModal}
                  />
                </div>
              )}

              {/* CALENDAR VIEW - Coming Soon */}
              {viewMode === 'calendar' && (
                <div className="mt-4">
                  <PackageSchedulingCalendarView
                    schedule={schedule}
                    bookingResults={bookingResults}
                    editingTreatment={editingTreatment}
                    setEditingTreatment={setEditingTreatment}
                    treatmentInstructions={treatmentInstructions}
                    handleTreatmentInstructionChange={handleTreatmentInstructionChange}
                  />
                </div>
              )}

              {/* Book Button */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                {/* Instructions Summary */}
                {(() => {
                  const instructionCount = Object.values(treatmentInstructions).filter(i => i && i.trim()).length;
                  return instructionCount > 0 && (
                    <div className="mb-3 text-sm text-gray-600">
                      ℹ️ {instructionCount} treatment(s) have specific instructions
                    </div>
                  );
                })()}

                {/* Booking progress is shown via the modal Dialog (see bottom of component) */}

                {/* Booking Results Summary - Only show after user attempts to book and not while in progress */}
                {bookingResults && hasAttemptedBooking && !bookingInProgress && (
                  <div className={`mb-3 p-3 rounded border ${
                    bookingResults.failed_count === 0 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-yellow-50 border-yellow-200'
                  }`}>
                    <div className="text-sm font-semibold mb-1">
                      {bookingResults.failed_count === 0 ? '✅ All Appointments Booked!' : '⚠️ Booking Completed with Issues'}
                    </div>
                    <div className="text-xs text-gray-700">
                      Booked: {bookingResults.booked_count} | Failed: {bookingResults.failed_count} | Success Rate: {bookingResults.summary?.success_rate || 'N/A'}
                    </div>
                    {bookingResults.failed_count > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs text-red-600 cursor-pointer">
                          View {bookingResults.failed_count} failed booking(s)
                        </summary>
                        <ul className="mt-2 space-y-1">
                          {bookingResults.failures.map((failure, idx) => (
                            <li key={idx} className="text-xs text-red-700">
                              • {failure.treatmentName} ({failure.date}): {failure.reason}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  disabled={bookingInProgress || !schedule}
                  onClick={handleBookAllAppointments}
                  style={{
                    backgroundColor: bookingInProgress || !schedule ? '#cccccc' : '#E4C984',
                    color: bookingInProgress || !schedule ? '#666666' : '#171717',
                    fontWeight: 'bold',
                    padding: '12px'
                  }}
                >
                  {bookingInProgress ? (
                    <span className="flex items-center gap-2">
                      <CircularProgress size={20} style={{ color: '#666666' }} />
                      Booking...
                    </span>
                  ) : (
                    'Book All Appointments'
                  )}
                </Button>
                
                {!schedule && (
                  <p className="text-sm text-gray-500 mt-2 text-center">
                    Generate a schedule first to enable booking
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              <div className="text-center">
                <CalendarMonthOutlinedIcon style={{ fontSize: 80, marginBottom: 16 }} />
                <p className="text-lg">Generate a schedule to preview</p>
                <p className="text-sm">Fill in the form and click "Generate Schedule"</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Booking Modal: in-progress (non-dismissable) → outcome (dismissable) */}
    <Dialog
      open={bookingInProgress || !!bookingOutcome}
      disableEscapeKeyDown={bookingInProgress}
      onClose={(_, reason) => {
        // Block all dismiss reasons while booking is in progress.
        if (bookingInProgress) return;
        // Allow backdrop / escape to close once outcome is shown.
        handleDismissBookingOutcome();
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 1
        }
      }}
    >
      <DialogContent sx={{ textAlign: 'center', py: 4 }}>
        {bookingInProgress ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={56} sx={{ color: '#E4C984' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
              Booking appointments...
            </Typography>
            {bookingChunkProgress && (
              <Typography variant="body2" sx={{ color: '#1d4ed8', fontWeight: 600 }}>
                Batch {bookingChunkProgress.current} of {bookingChunkProgress.total}
                {bookingChunkProgress.label ? ` — ${bookingChunkProgress.label}` : ''}
              </Typography>
            )}
            {(() => {
              const totalToBook = schedule?.schedule
                ? schedule.schedule.reduce((sum, day) => {
                    const unbookedOnDay = day.treatments.filter(t => {
                      return !existingBookings.some(b =>
                        b.date === day.date &&
                        b.treatment === t.name &&
                        b.status !== 'CANCELLED' &&
                        b.status !== 'CANCELED'
                      );
                    });
                    return sum + unbookedOnDay.length;
                  }, 0)
                : 0;
              return totalToBook > 0 ? (
                <Typography variant="body2" sx={{ color: '#4b5563' }}>
                  Processing {totalToBook} treatment{totalToBook !== 1 ? 's' : ''} across{' '}
                  {schedule?.schedule?.length || 0} day{(schedule?.schedule?.length || 0) !== 1 ? 's' : ''}
                </Typography>
              ) : null;
            })()}
            <Typography variant="caption" sx={{ color: '#6b7280', mt: 1, maxWidth: 320 }}>
              The scheduler finds therapist, room and time for each treatment.
              Very long itineraries are processed in automatic two-day batches so the browser connection does not time out; each batch may take up to two minutes.
            </Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
              Please do not close or refresh this window.
            </Typography>
          </Box>
        ) : bookingOutcome ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            {bookingOutcome.type === 'success' && (
              <>
                <Box sx={{
                  width: 64, height: 64, borderRadius: '50%',
                  bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 36, lineHeight: 1
                }}>✅</Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#166534' }}>
                  All appointments booked!
                </Typography>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  Successfully booked <strong>{bookingOutcome.bookedCount}</strong> of{' '}
                  <strong>{bookingOutcome.totalAttempted}</strong> appointments
                </Typography>
              </>
            )}

            {bookingOutcome.type === 'partial' && (
              <>
                <Box sx={{
                  width: 64, height: 64, borderRadius: '50%',
                  bgcolor: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 36, lineHeight: 1
                }}>⚠️</Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#92400e' }}>
                  Booking completed with issues
                </Typography>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  Booked <strong>{bookingOutcome.bookedCount}</strong> of{' '}
                  <strong>{bookingOutcome.totalAttempted}</strong> appointments
                </Typography>
              </>
            )}

            {bookingOutcome.type === 'error' && (
              <>
                <Box sx={{
                  width: 64, height: 64, borderRadius: '50%',
                  bgcolor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 36, lineHeight: 1
                }}>❌</Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>
                  Booking failed
                </Typography>
                <Typography variant="body2" sx={{ color: '#374151', maxWidth: 360 }}>
                  {bookingOutcome.message}
                </Typography>
              </>
            )}

            {/* Summary table for success / partial */}
            {(bookingOutcome.type === 'success' || bookingOutcome.type === 'partial') && (
              <Box sx={{
                width: '100%', mt: 1,
                bgcolor: '#f9fafb', borderRadius: 1, p: 2,
                display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.5, columnGap: 2,
                fontSize: '0.875rem'
              }}>
                <span style={{ color: '#6b7280' }}>Already booked</span>
                <strong style={{ textAlign: 'right' }}>{bookingOutcome.alreadyBooked}</strong>
                <span style={{ color: '#6b7280' }}>Newly booked</span>
                <strong style={{ textAlign: 'right', color: '#166534' }}>{bookingOutcome.bookedCount}</strong>
                <span style={{ color: '#6b7280' }}>Failed</span>
                <strong style={{
                  textAlign: 'right',
                  color: bookingOutcome.failedCount > 0 ? '#991b1b' : '#374151'
                }}>{bookingOutcome.failedCount}</strong>
                {bookingOutcome.successRate && (
                  <>
                    <span style={{ color: '#6b7280' }}>Success rate</span>
                    <strong style={{ textAlign: 'right' }}>{bookingOutcome.successRate}</strong>
                  </>
                )}
              </Box>
            )}

            {/* Compact failure list */}
            {bookingOutcome.type === 'partial' && bookingOutcome.failures?.length > 0 && (
              <Box sx={{
                width: '100%', mt: 1, textAlign: 'left',
                maxHeight: 160, overflowY: 'auto',
                border: '1px solid #fde68a', borderRadius: 1, p: 1.5,
                bgcolor: '#fffbeb'
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400e', display: 'block', mb: 0.5 }}>
                  {bookingOutcome.failedCount} could not be booked:
                </Typography>
                {bookingOutcome.failures.slice(0, 8).map((f, i) => (
                  <Typography
                    key={`${f.treatmentName}_${f.date}_${i}`}
                    variant="caption"
                    sx={{ display: 'block', color: '#7c2d12', lineHeight: 1.5 }}
                  >
                    • <strong>{f.treatmentName}</strong> ({f.date}): {f.reason}
                  </Typography>
                ))}
                {bookingOutcome.failures.length > 8 && (
                  <Typography variant="caption" sx={{ color: '#92400e', fontStyle: 'italic' }}>
                    …and {bookingOutcome.failures.length - 8} more (see schedule below)
                  </Typography>
                )}
              </Box>
            )}

            <Button
              onClick={handleDismissBookingOutcome}
              variant="contained"
              sx={{
                mt: 2,
                backgroundColor: '#E4C984',
                color: '#171717',
                fontWeight: 'bold',
                px: 5,
                '&:hover': { backgroundColor: '#cbb16f' }
              }}
            >
              OK
            </Button>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>

    {/* Guest Info Dialog */}
    <Dialog 
      open={guestInfoDialogOpen} 
      onClose={() => setGuestInfoDialogOpen(false)}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
        Guest Information
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {selectedGuestForInfo && (
          <Grid container spacing={3}>
            {/* Profile Picture */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar
                src={selectedGuestForInfo.profilePicture || selectedGuestForInfo.photoUrl}
                alt={selectedGuestForInfo.guestName}
                sx={{ width: 180, height: 180, mb: 2, border: '4px solid #E4C984' }}
              >
                {(!selectedGuestForInfo.profilePicture && !selectedGuestForInfo.photoUrl) && 
                  (selectedGuestForInfo.guestName || '?').charAt(0).toUpperCase()
                }
              </Avatar>
              
              {/* Info below photo */}
              <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center', mb: 1 }}>
                {selectedGuestForInfo.guestName}
              </Typography>
              {selectedGuestForInfo.room && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                  Room {selectedGuestForInfo.room}
                </Typography>
              )}
              {selectedGuestForInfo.program && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                  {selectedGuestForInfo.program}
                </Typography>
              )}
              {selectedGuestForInfo.nights && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {selectedGuestForInfo.nights} Nights
                </Typography>
              )}
            </Grid>

            {/* Guest Details */}
            <Grid item xs={12} md={8}>
              {/* General Instructions */}
              {(selectedGuestForInfo.generalInstructions || selectedGuestForInfo.instructions) && (
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#92400e' }}>
                    General Instructions
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {stripHtmlTags(selectedGuestForInfo.generalInstructions || selectedGuestForInfo.instructions)}
                  </Typography>
                </Box>
              )}

              {/* All Fields in 3 columns */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#374151' }}>
                All Fields
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(selectedGuestForInfo).map(([key, value]) => {
                  if (!value || key === 'profilePicture' || key === 'photoUrl') return null;
                  
                  const displayValue = typeof value === 'object' ? JSON.stringify(value) : stripHtmlTags(String(value));
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', display: 'block' }}>
                        {formatGuestFieldLabel(key)}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          whiteSpace: 'pre-wrap', 
                          wordBreak: 'break-word',
                          mt: 0.5
                        }}
                      >
                        {displayValue}
                      </Typography>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, backgroundColor: '#f9fafb' }}>
        <Button onClick={() => setGuestInfoDialogOpen(false)} sx={{ color: '#E4C984' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {/* Standalone Treatment Approval Modal */}
    <StandaloneTreatmentApprovalModal
      open={showStandaloneApprovalModal}
      onClose={handleStandaloneCancel}
      treatmentDetails={standaloneTreatmentData ? {
        name: standaloneTreatmentData.treatment.name,
        base_price: standaloneTreatmentData.treatment.base_price || standaloneTreatmentData.treatment.tier_value || 0,
        tax_rate: standaloneTreatmentData.treatment.tax_rate < 1 
          ? (standaloneTreatmentData.treatment.tax_rate * 100) 
          : (standaloneTreatmentData.treatment.tax_rate || 18)
      } : null}
      onApprove={handleStandaloneApprove}
    />

    <UpgradeApprovalModal
      open={showUpgradeModal}
      onClose={handleUpgradeCancel}
      upgradePreview={upgradePreview}
      onApprove={handleUpgradeApprove}
    />

    {/* Edit Booking Modal - For editing booked treatments */}
    {editingBooking && (
      <EditBookingModal
        booking={editingBooking}
        config={{
          treatments: availableTreatments,
          therapists: Object.values(config?.therapists || {}),
          rooms: allRoomsData
        }}
        show={showEditBookingModal}
        onClose={() => {
          setShowEditBookingModal(false);
          setEditingBooking(null);
        }}
        onDelete={(booking) => {
          // Handle delete from EditBookingModal
          if (window.confirm('Are you sure you want to delete this booking?')) {
            apiDelete(`/api/bookings/${booking.date}/${booking.id || booking.bookingId}`)
              .then(response => {
                if (response.ok) {
                  handleEditBookingComplete({ success: true });
                  alert('Booking deleted successfully');
                } else {
                  throw new Error('Failed to delete booking');
                }
              })
              .catch(error => {
                console.error('Error deleting booking:', error);
                alert(`Error deleting booking: ${error.message}`);
              });
          }
        }}
        onSchedulingComplete={handleEditBookingComplete}
      />
    )}

    {/* Retry Booking Modal - For finding alternative slots for failed treatments */}
    <Dialog 
      open={showRetryModal} 
      onClose={() => {
        setShowRetryModal(false);
        setRetryTreatmentData(null);
        setAvailableSlots([]);
        setRetryFailureHints([]);
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        🔄 Find Alternative Slots
        {retryTreatmentData && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {retryTreatmentData.treatment.name} on {retryTreatmentData.date}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {retryLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Searching for available slots...</Typography>
          </Box>
        ) : availableSlots.length > 0 ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Found {availableSlots.length} available time{availableSlots.length > 1 ? 's' : ''}. Click a slot to book.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
                alignItems: 'start',
              }}
            >
              {/* Morning column */}
              <Box
                sx={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: '#fafafa',
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: '#fffbeb',
                    borderBottom: '1px solid #fde68a',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e' }}>
                    ☀️ Morning
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Before 2:00 PM
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 80 }}>
                  {retrySlotsByPeriod.morning.length > 0 ? (
                    retrySlotsByPeriod.morning.map((slot, index) => renderRetrySlotCard(slot, index))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No morning slots
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Afternoon column */}
              <Box
                sx={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: '#fafafa',
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: '#fff7ed',
                    borderBottom: '1px solid #fed7aa',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#9a3412' }}>
                    🌤️ Afternoon
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    From 2:00 PM
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 80 }}>
                  {retrySlotsByPeriod.afternoon.length > 0 ? (
                    retrySlotsByPeriod.afternoon.map((slot, index) => renderRetrySlotCard(slot, index))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No afternoon slots
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No available slots found with current or relaxed constraints.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try a different date or contact admin to adjust therapist schedules.
            </Typography>
            {retryFailureHints.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  textAlign: 'left',
                  bgcolor: 'rgba(245, 158, 11, 0.08)',
                  border: '1px solid rgba(245, 158, 11, 0.35)',
                  borderRadius: 1,
                  maxHeight: 220,
                  overflow: 'auto'
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  Scheduler responses (check browser Console for full logs):
                </Typography>
                {retryFailureHints.map((h, i) => (
                  <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    {h}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => {
            setShowRetryModal(false);
            setRetryTreatmentData(null);
            setAvailableSlots([]);
            setRetryFailureHints([]);
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default PackageScheduling;

